/**
 * Hand-maintained mirror of the FastAPI Pydantic models in `server/app/models/api/`.
 *
 * No script generates these types. When a backend API model changes, change its
 * mirror here in the same commit. A few sub-shapes mirror the
 * `server/app/models/db/` models that the API models embed.
 */

export interface CurrentUser {
  sub: string;
  username: string | null;
  scopes: readonly string[];
  groups: readonly string[];
}

/**
 * Spoken-language preference for phone calls. Mirrors the `PreferredLanguage`
 * literal in server/app/models/db/enrollment.py.
 *
 * The stored and wire value is always the lowercase English key; only the
 * displayed label is localized. Carried flat on the patient and nested on the
 * caregiver. A row without the attribute reads back `"english"`.
 */
export type PreferredLanguage = "english" | "spanish" | "korean" | "mandarin";

/**
 * The public create payload: the form-visible subset of the customer form's
 * `EnrollmentFormValues`, defined alongside the schema in
 * `../lib/enrollmentSchema`.
 *
 * Mirrors `EnrollmentCreate` in server/app/models/api/enrollments.py, which
 * rejects the admin-only fields. Re-exported here so callers can import it
 * from `@dearbill/shared`.
 */
export type { EnrollmentCreate } from "../lib/enrollmentSchema";

export interface EnrollmentRead {
  id: string;
  created_at: string;
  /**
   * Server-built absolute URL of the member portal root (`{MEMBER_BASE_URL}/`).
   * Always present; the frontend renders it as an `href` and never builds it.
   */
  memberStatusUrl: string;
  /**
   * Short-lived bearer for the M3P flow's in-house e-sign call, sent as
   * `Authorization: Bearer …` on `POST /enrollments/{id}/sign`. Non-null only
   * for a `flow: "m3p"` create; the `/form` path signs in the member portal
   * instead. Mirrors `EnrollmentRead.sign_token` in
   * server/app/models/api/enrollments.py.
   */
  signToken: string | null;
  /**
   * Single-use token the Done screen's dashboard link appends to
   * `memberStatusUrl` as a URL fragment (`#handoff=<token>`), so it never
   * reaches a server or a `Referer`. On arrival the member SPA exchanges it at
   * `POST /api/portal/login/handoff` for a portal session. It lives for the
   * server's `portal_handoff.TTL_SECONDS`. Non-null only for a `flow: "m3p"`
   * create whose patient row carries an MBI; otherwise null, and the link stays
   * plain. Mirrors `EnrollmentRead.portal_handoff_token` in
   * server/app/models/api/enrollments.py.
   */
  portalHandoffToken: string | null;
  /**
   * The contract year the accepted payload picked its plan for (the create's
   * `planYear`, stored as `EnrollmentRecord.plan_year`); null reads as the
   * current year. Mirrors `EnrollmentRead.plan_year` in
   * server/app/models/api/enrollments.py.
   */
  planYear: number | null;
}

/**
 * Body of `POST /enrollments/{id}/sign`: the M3P flow's in-house e-sign of the
 * prefilled CMS-10882.
 *
 * Authorized by the create response's `signToken` bearer, not a portal
 * session. Same shape as `PortalSignRequest`. Mirrors `EnrollmentSignRequest`
 * in server/app/models/api/enrollments.py.
 */
export interface EnrollmentSignRequest {
  /** Canvas PNG as base64. The client omits the `data:image/png;base64,` prefix; the server strips one if present. */
  signaturePngBase64: string;
  /** The signer's typed full legal name. */
  typedName: string;
  /** Affirmative consent to sign electronically; must be `true`. */
  intent: boolean;
}

/**
 * `200` response of `POST /enrollments/{id}/sign`. A repeat sign returns `409`
 * (already signed), which the flow treats as success. Mirrors
 * `EnrollmentSignResult` in server/app/models/api/enrollments.py.
 */
export interface EnrollmentSignResult {
  id: string;
  /** ISO timestamp at which the signature was recorded. */
  signedAt: string;
}

/**
 * Body of the public `POST /api/enrollments/decode-ref`. `refId` is the opaque
 * referral JWT from the `?refId=` query param of a partner-shared link.
 */
export interface DecodeRefRequest {
  refId: string;
}

/**
 * Response of `POST /api/enrollments/decode-ref`, always `200`. Mirrors
 * `DecodeRefResponse` in server/app/models/api/enrollments.py.
 *
 * On a valid, unexpired token every field is set and `reason` is `null`. On an
 * invalid, expired or used token `valid` is `false`, the identity fields are
 * `null`, and `reason` says why. The customer form shows a referral banner and
 * a live countdown from this, and blocks submit when the token is bad.
 *
 * The call claims the token: the first call marks it accessed, and any later
 * call returns `{ valid: false, reason: "used" }`.
 */
export interface DecodeRefResponse {
  valid: boolean;
  orgName?: string | null;
  memberName?: string | null;
  /** Epoch seconds, not ms, at which the referral token expires. */
  expiresAt?: number | null;
  /**
   * Set only when `valid` is true: the minting org's partner-portal forms view
   * (`{partnerBaseUrl}/orgs/{orgId}/forms`), built server-side. The frontend
   * renders it as an `href` and never builds it.
   */
  partnerPortalUrl?: string | null;
  reason?: "expired" | "invalid" | "used" | null;
  // Referrer org metadata, with no patient PHI; all null when `valid` is false.
  /** Referrer org type; the form shows the pharmacy or broker consent checkboxes from it. */
  organizationType?: OrgType | null;
  /** Referrer org's legal name, distinct from the display `orgName`; the consent labels use it. */
  legalName?: string | null;
  /** Referrer org's contact email; `""` when the org profile has none. */
  email?: string | null;
  /** Broker referrers only: the state codes the broker is licensed in; the form blocks other states. */
  licensedStates?: string[] | null;
}

/**
 * Structured `detail` of the `409` from `POST /api/enrollments` when the backend
 * rejects a supplied `refId` at submit time; no enrollment is written.
 * `ApiError.body` carries `{ detail: ReferralRejectDetail }`, and the form maps
 * the code to its expired, invalid or used message and blocks resubmit.
 * `referral_used` means the token's access was already claimed.
 */
export interface ReferralRejectDetail {
  code: "referral_expired" | "referral_invalid" | "referral_used";
}

/**
 * Address sub-models, mirrored from server/app/models/api/enrollments.py. The
 * customer enrollment form and the admin read APIs both use these wire shapes.
 *
 * `MailingAddress` is all strings, with no required-string narrowing, because
 * the backend treats blank fields as "not provided" when
 * `hasSeparateMailingAddress` is false. The admin types use
 * `MailingAddress | null` to express that distinction.
 */
export interface PermanentAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

export interface MailingAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

export interface PharmacyAddress {
  street1: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Preferred pharmacy block. Mirrors the `Pharmacy` sub-model in
 * server/app/models/api/enrollments.py.
 *
 * `name` is always present (`""` by default). `phone` is an optional E.164
 * number, stored as null when blank. The structured `prescriptionsList` is a
 * flat sibling on the enrollment, not a pharmacy attribute.
 */
export interface Pharmacy {
  name: string;
  phone: string | null;
  address: PharmacyAddress;
}

/**
 * One drug snapshot on an enrollment's `prescriptionsList`. Mirrors the
 * `Prescription` sub-model in server/app/models/db/enrollment.py.
 *
 * The snapshot is copied from the drug catalog when the drug is added (an
 * admin's Add Drug pick, the M3P flow's picks at create, or the member
 * portal's add bar), so the read, portal and partner paths never join back to
 * the drug reference table.
 */
export interface Prescription {
  /** RxNorm concept id; the admin Add Drug modal de-dups on it. */
  rxcui: string;
  /** Shown in the admin list and on the member dashboard. */
  drugName: string;
  /** Shown in the admin list and on the member dashboard. */
  label: string;
  /** Dosage form, possibly blank. The partner detail shows it, and the member dashboard falls back to it when `label` is blank. */
  form: string;
  /**
   * When the drug joined the list, ISO 8601 UTC. Server-owned: the create, the
   * member add and the admin whole-list PUT stamp it, and a client value is
   * ignored. Null on rows written before the field existed; the member portal
   * then shows the enrollment's `createdAt`.
   */
  addedAt: string | null;
}

/**
 * One row of the drug reference table. Mirrors `Drug` in
 * server/app/models/api/drugs.py.
 *
 * `GET /api/admin/drugs` returns a bare JSON array of these. The quarterly
 * formulary refresh rebuilds the table from the CMS SPUF formulary and RxNorm
 * Prescribable Content.
 */
export interface Drug {
  rxcui: string;
  drugName: string;
  activeIngredients: string;
  kind: string;
  label: string;
  form: string;
  dosage: string;
  route: string;
  /** RxNorm `RXN_QUANTITY` fill quantity (e.g. "3 ML"); null when RxNorm has none. The server derives the one-fill quantity behind `M3pProjectionDrug.gross` from it; not rendered. */
  rxnQuantity: string | null;
  /** Verbatim RxNorm concept name (e.g. "3.2 ML aripiprazole 300 MG/ML Prefilled Syringe [Abilify]"), shown on the admin plan formulary; null when the stored row lacks it. */
  rxnormName: string | null;
}

/**
 * The tier's cost-sharing rule for one covered drug. Mirrors
 * `MemberCostBreakdown` in server/app/models/api/drugs.py.
 *
 * The server interprets the CMS codes (channel fallback, copay or
 * coinsurance), and the SPA only formats. A null `channel` means the tier is
 * offered on neither retail channel. `amount` is copay dollars, or the raw
 * coinsurance fraction (`0.25` = 25%). `minAmount` and `maxAmount` bound a
 * coinsurance in dollars (null = no bound). `insulinRule` means the plan's own
 * insulin rows govern the drug: the member pays the lesser of copay and
 * coinsurance.
 */
export interface MemberCostBreakdown {
  channel: "preferred_retail" | "standard_retail" | null;
  costType: "copay" | "coinsurance" | null;
  amount: number | null;
  minAmount: number | null;
  maxAmount: number | null;
  deductibleApplies: boolean | null;
  specialty: boolean | null;
  insulinRule: boolean;
}

/**
 * Coverage for one drug under one plan. Mirrors `DrugCoverageEntry` in
 * server/app/models/api/drugs.py.
 *
 * Fields degrade to null, never a 500. When the plan is unknown for the
 * quarter every field is null (`covered: null` is not "not covered"). An
 * on-formulary drug carries its tier, pa/st/ql flags and the tier's
 * cost-sharing rule. A drug absent from the plan's formulary is
 * `covered: false` with everything else null. Money lives on
 * `M3pProjectionDrug`; this entry carries none.
 */
export interface DrugCoverageEntry {
  rxcui: string;
  /** null = unknown; false = not on this plan's formulary. */
  covered: boolean | null;
  tier: number | null;
  /** Prior authorization required. */
  pa: boolean | null;
  /** Step therapy required. */
  st: boolean | null;
  /** Quantity limit applies. */
  ql: boolean | null;
  /** The tier's cost-sharing rule; non-null exactly when `covered` is true. */
  memberCostBreakdown: MemberCostBreakdown | null;
}

/**
 * The shared drug-coverage envelope. Mirrors `DrugCoverageResponse` in
 * server/app/models/api/drugs.py.
 *
 * Returned by the public
 * `GET /api/plans/{planId}/drug-coverage?rxcuis=a,b,c[&planYear=Y]` (one entry
 * per requested rxcui, deduped; a blank list or more than the server's
 * `MAX_COVERAGE_RXCUIS` is a 422; a `planYear` with no next-year data is a 422
 * `plan_year_unavailable`) and by the admin
 * `GET /api/admin/enrollments/{id}/drug-coverage` (one entry per
 * prescription). The sibling public drug search, `GET /api/drugs?search=<q>`,
 * returns a plain `Drug[]` of at most the server's `SEARCH_LIMIT` rows; a
 * blank search is a 422.
 *
 * `coverageKnown: false` means the plan couldn't be resolved to the quarter's
 * snapshot (no assigned plan, dangling or blank CMS ids, or the plan is absent
 * from the SPUF quarter), and every entry is then all-null. `asOf` is the
 * ingested SPUF quarter (e.g. `2026Q2`, the UI's "data as of" stamp), null
 * before the first refresh. `contractYear` is the snapshot's contract year:
 * next year's when the request resolved to the next-year source.
 */
export interface DrugCoverageResponse {
  coverageKnown: boolean;
  asOf: string | null;
  contractYear: number | null;
  drugs: DrugCoverageEntry[];
}

/**
 * One drug's row in the M3P projection: the member's out-of-pocket for one
 * fill of that drug. Mirrors `M3pProjectionDrug` in
 * server/app/models/api/drugs.py.
 *
 * Rows are allocated in request order: the deductible is consumed and the OOP
 * threshold clipped sequentially, so the rows sum to `totalOop` before
 * per-field cents rounding.
 */
export interface M3pProjectionDrug {
  rxcui: string;
  /** The catalog name on the public endpoint, or the `Prescription` snapshot's on the dashboard; null when unknown. */
  drugName: string | null;
  label: string | null;
  covered: boolean;
  tier: number | null;
  /** The one-fill plan price (unit cost × the fill quantity); null = unpriced. */
  gross: number | null;
  deductiblePaid: number | null;
  /** The copay, bounded coinsurance or insulin rule on the remainder. */
  costShare: number | null;
  /** deductiblePaid + costShare, clipped to what is left below the OOP threshold: the member's out-of-pocket for this fill with the deductible unmet, the high end of the range. */
  cost: number | null;
  /** The same fill priced with the deductible already met: the low end of the `costMin`–`cost` range. Null whenever `cost` is null; equal to `cost` when the YTD is known (`ytdKnown`). */
  costMin: number | null;
  /** cost / monthsRemaining. */
  monthly: number | null;
  /** costMin / monthsRemaining; null whenever `monthly` is null. */
  monthlyMin: number | null;
}

/**
 * The one-fill M3P payment projection. Mirrors `M3pProjection` in
 * server/app/models/api/drugs.py.
 *
 * Returned by the public
 * `GET /api/plans/{planId}/m3p-projection?rxcuis=a,b[&candidates=c,d][&ytdTroop=N][&planYear=Y]`
 * (`rxcuis` may be empty when `candidates` is set; a `planYear` with no
 * next-year data is a 422 `plan_year_unavailable`) and the admin
 * `GET /api/admin/enrollments/{id}/m3p-projection`, and carried as
 * `PortalDashboard.m3p`. Each drug counts one fill (unit cost × the one-fill
 * quantity, no refills), run once through the plan's benefit design in request
 * order: the deductible first, then per-tier copay or coinsurance, then $0 once
 * TrOOP reaches the OOP threshold. What the member already spent this year is
 * subtracted. `totalOop` is that single pass, and `monthlyPayment` spreads it
 * over the months left in the year. Fields degrade to null, never a 500: an
 * unknown plan, an unpriced needed drug or no included drugs blank `totalOop`
 * and `monthlyPayment` and set `unavailableReason`.
 *
 * Under the next-year source (`pricesEstimated`) the snapshot is next contract
 * year's rules and formulary, whose monthly file carries no prices, so the
 * grosses use `priceYear`'s unit costs; the clock is that January 1
 * (`startMonth` 1, `monthsRemaining` 12) and `ytdKnown` holds with nothing
 * spent, so every `*Min` equals its max.
 */
export interface M3pProjection {
  coverageKnown: boolean;
  /** SPUF quarter, e.g. "2026Q2". */
  asOf: string | null;
  contractYear: number | null;
  /** 1–12, the current month (server clock, UTC). */
  startMonth: number;
  /** 12 - startMonth + 1 (the current month counts; December = 1). */
  monthsRemaining: number;
  deductible: number | null;
  oopThreshold: number | null;
  /** What the math subtracted (0 when unknown). */
  ytdTroop: number;
  /** True when real PDE data backed `ytdTroop`. */
  ytdKnown: boolean;
  /** True when the prices are the current year's applied to next year's rules (the next-year source). */
  pricesEstimated: boolean;
  /** The price table's contract year; null before its first ingest. */
  priceYear: number | null;
  /** Σ one-fill member cost over the included drugs, in one threshold-clipped pass with the deductible unmet: the high end. */
  totalOop: number | null;
  /** The same total with the deductible already met: the low end. Null whenever `totalOop` is null; equal to it when `ytdKnown`. */
  totalOopMin: number | null;
  /** totalOop / monthsRemaining: the one-time bill spread over the months left. */
  monthlyPayment: number | null;
  /** totalOopMin / monthsRemaining; null whenever `monthlyPayment` is null. */
  monthlyPaymentMin: number | null;
  /** `covered: false` drugs left out (the footnote count). */
  excludedRxcuis: string[];
  unavailableReason: "plan_unknown" | "unpriced_drug" | "no_drugs" | null;
  /** One per requested rxcui, request order (`covered: false` rows included). */
  drugs: M3pProjectionDrug[];
  /** One per `candidates` rxcui, each priced as if appended to `drugs`; `[]` without the param. */
  candidates: M3pProjectionDrug[];
}

/**
 * One row of the admin plan formulary table: a `Drug` plus its
 * `DrugCoverageEntry` under that plan, carried in
 * `PlanFormularyDrugsResponse.drugs`. Mirrors `PlanFormularyDrug` in
 * server/app/models/api/drugs.py.
 */
export interface PlanFormularyDrug extends Drug {
  coverage: DrugCoverageEntry;
  /**
   * The drug's standalone one-fill member cost: nothing else selected, a YTD
   * TrOOP of $0, and the full deductible unmet.
   */
  oneFill: M3pProjectionDrug | null;
}

/**
 * Response of `GET /api/admin/plans/{planId}/formulary-drugs`. Mirrors
 * `PlanFormularyDrugsResponse` in server/app/models/api/drugs.py.
 *
 * Every catalog drug on the plan's formulary, sorted by `drugName` then
 * `label`, each with `coverage.covered === true` and `oneFill` set. An unknown
 * plan id is a 404; a plan the quarter's snapshot doesn't know returns
 * `coverageKnown: false` with empty `drugs`. `asOf` is the ingested SPUF
 * quarter. `suppressed` (SPUF `PLAN_SUPPRESSED_YN`) means the plan is in the
 * snapshot but CMS published no formulary or pricing rows for it; always false
 * when `coverageKnown` is false.
 */
export interface PlanFormularyDrugsResponse {
  coverageKnown: boolean;
  asOf: string | null;
  suppressed: boolean;
  drugs: PlanFormularyDrug[];
}

/**
 * Plan-level cost sharing from `GET /api/admin/plans/{planId}/cost-sharing`.
 * Mirrors `PlanCostSharing` and its nested `PlanLandscapeRead`, `TierCostRow`,
 * `ChannelCostRead`, `InsulinCostRow` and `InsulinChannelRead` in
 * server/app/models/api/drugs.py. Reference data with no PHI.
 *
 * The wire is already interpreted: level names instead of CMS codes, day
 * counts instead of days-supply codes, and a `null` channel for one the plan
 * doesn't offer (CMS `COST_TYPE 0`), so the SPA only renders. An unknown plan
 * id is a 404; a plan outside the quarter's snapshot returns
 * `coverageKnown: false` with empty `tiers` and `insulin` and
 * `landscape: null`. Rows arrive sorted by (level, tier, days). `landscape` is
 * null when the CMS Landscape file had no row for the plan segment.
 */
export interface PlanCostSharing {
  coverageKnown: boolean;
  /** The ingested SPUF quarter, e.g. `2026Q2`; null before the first refresh. */
  asOf: string | null;
  /** CMS-suppressed plan (SPUF `PLAN_SUPPRESSED_YN`): no rows published; false when `coverageKnown` is false. */
  suppressed: boolean;
  contractYear: number | null;
  /** SPUF PREMIUM = the Part D total premium (monthly). */
  premium: number | null;
  /** SPUF DEDUCTIBLE = the Part D annual deductible. */
  deductible: number | null;
  landscape: PlanLandscape | null;
  tiers: PlanTierCostRow[];
  insulin: PlanInsulinCostRow[];
}

/** The CMS Landscape file's Part D fields only, with no MA MOOP, Part C or star ratings. */
export interface PlanLandscape {
  /** The annual Part D out-of-pocket cap, the same for every Part D plan. */
  oopThreshold: number | null;
  /** May be negative (a premium credit). */
  partDBasicPremium: number | null;
  partDSupplementalPremium: number | null;
  partDTotalPremium: number | null;
  partDLowIncomePremium: number | null;
  /** `Enhanced Alternative` | `Defined Standard` | `Basic Alternative` | `Actuarially Equivalent Standard`. */
  drugBenefitType: string | null;
  /** `Basic` | `Enhanced`. */
  drugBenefitCategory: string | null;
  /** Yes/No; null for CMS `Not Applicable`. */
  offersNoDeductibleTier: boolean | null;
}

/** One channel's cost sharing on a tier row (server `ChannelCostRead`); the row's channel is null when not offered. */
export interface ChannelCost {
  costType: "copay" | "coinsurance";
  /** Copay dollars, or the raw coinsurance fraction (`0.25` = 25%); null when CMS left the cell blank. */
  amount: number | null;
  /** Dollar bounds on a coinsurance; null = no bound. */
  minAmount: number | null;
  maxAmount: number | null;
}

export type CoverageLevel = "pre_deductible" | "initial" | "catastrophic";
/** Day counts; null = the CMS "other" days-supply code. */
export type DaysSupply = 30 | 60 | 90 | null;

/** One SPUF Beneficiary Cost row: a (level, tier, days-supply) cell across the four pharmacy channels. */
export interface PlanTierCostRow {
  coverageLevel: CoverageLevel;
  /** null when CMS leaves TIER blank (defined-standard shape). */
  tier: number | null;
  daysSupply: DaysSupply;
  specialty: boolean;
  deductibleApplies: boolean;
  preferredRetail: ChannelCost | null;
  standardRetail: ChannelCost | null;
  preferredMail: ChannelCost | null;
  standardMail: ChannelCost | null;
}

/** The insulin lesser-of inputs for one channel: the member pays min(copay, coinsurance × price). */
export interface InsulinChannelCost {
  /** Dollars per this days-supply. */
  copay: number | null;
  /** The raw fraction. */
  coinsurance: number | null;
}

/** One SPUF Insulin Beneficiary Cost row; a null channel = not offered. */
export interface PlanInsulinCostRow {
  tier: number | null;
  daysSupply: DaysSupply;
  preferredRetail: InsulinChannelCost | null;
  standardRetail: InsulinChannelCost | null;
  preferredMail: InsulinChannelCost | null;
  standardMail: InsulinChannelCost | null;
}

/**
 * Response of `GET /api/admin/drugs/refresh-status`: the CMS quarterly
 * formulary-refresh status singleton. Mirrors `FormularyRefreshStatus` in
 * server/app/models/api/drugs.py.
 *
 * Field for field the `PlansRefreshStatus` contract plus `sourceFile`, but not
 * extended from it, because the two mirror independent server models.
 * `POST /api/admin/drugs/refresh` (super-admin only) returns 202 with an
 * optimistic `running` status, 409 while a refresh is running, and 503 when
 * the refresh task isn't configured or the launch fails. `counts` keys are the
 * snake_case `FormularyIngestResult` counters (`drugs_written`, `formularies`,
 * `plans`, …).
 *
 * `nextYear` is next contract year's table's own status row (the monthly-file
 * stage) in the same shape: its `reportPeriod` reads `"2027 (2027_20261008)"`,
 * its `note` says why a run had nothing to ingest, and its own `nextYear` is
 * always null. Null until a run has looked for next year's file.
 */
export interface FormularyRefreshStatus {
  state: "never_run" | "running" | "succeeded" | "failed";
  startedAt: string | null;
  finishedAt: string | null;
  /** SPUF report quarter, e.g. `2026Q2`; with `sourceFile`, the "data as of" stamp. */
  reportPeriod: string | null;
  /** The discovered SPUF source zip name. */
  sourceFile: string | null;
  counts: Record<string, number> | null;
  error: string | null;
  violations: string[] | null;
  /** Email of the admin who triggered the run; null when not triggered by an admin. */
  triggeredBy: string | null;
  /** Why a run had nothing to ingest; set on the next-year row only. */
  note: string | null;
  nextYear: FormularyRefreshStatus | null;
}

/**
 * Authorized-representative (caregiver) mailing address for CMS-10882 page 2.
 * Mirrors `CaregiverAddress` in server/app/models/api/enrollments.py, the
 * read side of `PortalCaregiver.address`.
 *
 * Same all-strings shape as `MailingAddress`: a stored block may carry blank
 * address fields, and `street2` is `""` when absent. The add and edit bodies
 * use the stricter `CaregiverAddressWrite`.
 */
export interface CaregiverAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Partner-referral attribution on an enrollment. Mirrors the `PartnerReferrer`
 * sub-model in server/app/models/db/enrollment.py.
 *
 * Records which partner org and partner user referred a patient; the back
 * office shows "Individual" or the partner from it. `partnerReferrer` is `null`
 * for an Individual enrollment, so render defensively.
 *
 * `via` says how the org is attributed:
 *   - `"referral"`: a staff member minted a `?refId=` link, and
 *     `partnerUserId`/`partnerUserName` name that member. A row without `via`
 *     reads back `"referral"`.
 *   - `"self"`: the patient self-enrolled through the org's printed `?selfId=`
 *     QR code, so there is no referring member and both fields are `null`.
 */
export interface PartnerReferrer {
  partnerId: string;
  partnerName: string;
  /** Null for a self-enrollment (no referring member). */
  partnerUserId: string | null;
  /** Null for a self-enrollment (no referring member). */
  partnerUserName: string | null;
  via: "referral" | "self";
}

/**
 * Response of the partner and admin self-enroll-code endpoints
 * (`GET .../self-enroll-code`). Mirrors `PartnerSelfEnrollCodeResponse` in
 * server/app/models/api/partner.py.
 *
 * `code` is the org's opaque self-enroll id (`selfId`). `url` is the
 * server-built customer link with the code in its `?selfId=` param
 * (`{app_base_url}/programs?selfId=<code>`, the M3P flow). The frontend renders
 * and QR-encodes it as-is and never builds it.
 */
export interface SelfEnrollCode {
  code: string;
  url: string;
}

/**
 * Response of the public `GET /api/enrollments/decode-self?selfId=<code>`.
 * Mirrors `DecodeSelfResponse` in server/app/models/api/enrollments.py.
 *
 * Unlike `decode-ref` this is an idempotent GET that never burns the code, and
 * a bad code is an HTTP error: `404` for an unknown or retired code, `409` when
 * the org can't take referrals (disabled, or a Pharmacy or Broker org that
 * isn't active). On `200` the customer SPA shows the self-enroll banner.
 */
export interface DecodeSelfResponse {
  orgName: string;
  /** Org type; a Broker org adds the broker consent checkboxes and the licensed-state check. */
  organizationType: OrgType;
  legalName: string;
  /** Broker orgs only: the licensed state codes; null/absent otherwise. */
  licensedStates?: string[] | null;
}

/**
 * Structured attestation of the pharmacy consent to share information. Mirrors
 * the `PharmacyAgreement` sub-model in server/app/models/db/enrollment.py.
 *
 * Set server-side only on a Pharmacy-referred enrollment, when the patient
 * ticks the consent checkbox on the enroll form (copy key
 * `enroll.consent.pharmacy` in `frontend/shared/src/i18n/messages.ts`); `null`
 * on every other row. No PDF is rendered, so this record is the only durable
 * evidence of consent. The server snapshots the pharmacy identity
 * (`pharmacyLegalName`, `pharmacyEmail`) from the resolved referrer org and
 * never takes it from the client. The admin enrollment detail shows it
 * read-only.
 */
export interface PharmacyAgreement {
  agreed: boolean;
  /** UTC ISO-8601 acceptance timestamp. */
  agreedAt: string;
  /** Version stamp of the consent copy (server `PHARMACY_CONSENT_VERSION`). */
  documentVersion: string;
  pharmacyLegalName: string;
  pharmacyEmail: string;
}

/**
 * Broker-referral consent attestation. Mirrors the `BrokerAgreement` sub-model
 * in server/app/models/db/enrollment.py.
 *
 * Set server-side on every Broker-referred enrollment (refId or selfId path)
 * from the required representative-consent checkbox. `shareResults` is the
 * optional "share my results with my broker" tick, which gates the partner
 * portal view. The server snapshots the broker identity from the resolved org.
 */
export interface BrokerAgreement {
  agreed: boolean;
  /** UTC ISO-8601 acceptance timestamp. */
  agreedAt: string;
  /** Version stamp of the consent copy (server `BROKER_CONSENT_VERSION`). */
  documentVersion: string;
  brokerLegalName: string;
  brokerNpn: string;
  shareResults: boolean;
}

/**
 * E-sign (CMS-10882) status. Mirrors `EsignStatus` in
 * server/app/models/db/enrollment.py.
 *
 * The patient signs in the member portal (`PortalSignRequest`) or in the M3P
 * enrollment flow (`EnrollmentSignRequest`). `sent` is written by the admin
 * notify-sign-request endpoint, and `expired` has no writer.
 */
export type EsignStatus = "none" | "sent" | "signed" | "expired";

/**
 * Enrollment lifecycle status. Mirrors `EnrollmentStatus` in
 * server/app/models/db/enrollment.py. It lives in shared because the admin and
 * partner SPAs render it through `statusMap` (`shared/src/lib/format.ts`).
 */
export type EnrollmentStatus =
  // A lead-capture enrollment the M3P flow's Blue Button callback writes;
  // `POST /enrollments` turns it into `new` in place.
  | "draft"
  | "new"
  | "pending_m3p_enrollment"
  | "pending_lis_enrollment"
  | "pending_pap_enrollment"
  | "need_more_information"
  | "denied_m3p_enrollment"
  | "denied_lis_enrollment"
  | "denied_pap_enrollment"
  | "member_withdrew"
  | "enrolled_m3p"
  | "enrolled_lis"
  | "enrolled_pap";

/**
 * Runtime configuration from `GET /admin/config`, which the admin SPA fetches
 * once on mount. Mirrors `AdminConfig` in server/app/models/api/admin.py.
 * `callerNumber` is the Telnyx outbound caller number; empty when Telnyx isn't
 * configured. `appEnv` is the server's `APP_ENV` (`local`, `dev` or `prod`);
 * the Feature flags page shows its Danger zone only for `local` and `dev`.
 */
export interface AdminConfig {
  callerNumber: string;
  appEnv: string;
}

/**
 * Response of `POST /api/admin/maintenance/nuke`, the dev-only wipe of every
 * transactional table and upload. Mirrors `NukeResult` in
 * server/app/models/api/maintenance.py. `tables` maps each wiped table's logical
 * name (`enrollments`, `patients`, `calls`, `contactus`, `partners`, `gpos`,
 * `partner_signups`, `bb`, `sms_consent`, `rate_limits`) to the number of
 * items deleted; `s3Objects` is the number of uploads deleted. The route is
 * mounted only where `AdminConfig.appEnv` is `local` or `dev` (404 elsewhere),
 * super-admin only (403) and refuses a body other than `{confirm: "NUKE"}`
 * (400).
 */
export interface NukeResult {
  tables: Record<string, number>;
  s3Objects: number;
}

/**
 * Admin-management wire shapes, mirrored from server/app/models/api/admin.py.
 * `AdminPublic` omits the DB-internal `google_sub` on purpose.
 */
export type AdminRole = "admin" | "super_admin";

export interface AdminPublic {
  email: string;
  name: string | null;
  addedBy: string | null;
  addedAt: string;
  disabled: boolean;
  role: AdminRole;
}

export interface AdminCreate {
  email: string;
  name: string | null;
}

/** Body of `PATCH /api/admin/admins/{email}`: change an admin's role. */
export interface AdminRoleUpdate {
  role: AdminRole;
}

/**
 * Response of `GET /api/admin/me`: the admin SPA's access probe and role
 * source. Every authenticated admin can call it.
 */
export interface AdminMe {
  email: string;
  role: AdminRole;
}

/**
 * Insurers and plans catalogue wire shapes for the admin `/api/admin/insurers`
 * and `/api/admin/plans` surface, mirrored from server/app/models/api/plan.py.
 *
 * Plans come from the CMS monthly-report ingest and are read-only on the wire
 * except `notes`: there are no plan create or delete endpoints, and
 * `PlanWrite` is the notes-only PUT body.
 *
 * Naming notes:
 *   1. The catalogue insurer type is `InsurerRead`, matching the server model;
 *      there is no `Insurer` type.
 *   2. `PlanAuditEntry.diff` keys are the raw snake_case record field names
 *      (e.g. `plan_name`, `cms_plan_id`, `stedi_id`), not camelCase. Prettify
 *      them to labels when rendering. `diff` is present only on `update`
 *      entries.
 */
export type PlanAuditAction = "create" | "update" | "deactivate" | "reactivate";
export type PaymentOptionType = "phone" | "mail" | "portal";

/**
 * The coverage-category taxonomy (docs/plans/CMS_PLANS_INGESTION_README.md).
 * `TM` and `NI` are patient-level determinations, in the enum only so patient
 * and plan records share one vocabulary; they never appear on plan rows.
 */
export type PlanCategory =
  | "MA-PD"
  | "MA"
  | "DUAL"
  | "EGMA-PD"
  | "EGMA"
  | "EG-PD"
  | "TM-PD"
  | "PACE"
  | "SMA"
  | "SMA-PD"
  | "TM"
  | "NI";

/**
 * One insurer payment channel, with exactly these two keys: no per-option note
 * or label, since extra detail lives in the insurer-level `notes`. Duplicate
 * types are allowed and entry order is preserved. A `portal` value is an
 * `http(s)://` URL; `value` is never blank.
 */
export interface PaymentOption {
  type: PaymentOptionType;
  value: string;
}

/** Read-only insurer summary embedded on every `Plan` (server-side join). */
export interface PlanInsurerSummary {
  id: string;
  name: string;
  logoUrl: string | null;
}

/**
 * Full read projection of one CMS-sourced plan (`GET`/`PUT` response). Mirrors
 * `PlanRead` in server/app/models/api/plan.py.
 */
export interface Plan {
  id: string;
  /** May be `""` (contract-level HCPP rows); the UI falls back to the insurer name. */
  planName: string;
  /** Embedded read-only summary of the owning insurer (server-side join). */
  insurer: PlanInsurerSummary;
  /** CMS contract number, e.g. `H3379`, or an all-numeric HCPP contract number. */
  cmsContractNumber: string;
  /** 3-digit PBP id, e.g. `053`; may be `""` for contract-level HCPP rows. */
  cmsPlanId: string;
  category: PlanCategory;
  /** True when the CMS plan type was unrecognized, so the category needs human review. */
  categoryReviewNeeded: boolean;
  /** Raw CMS plan-type string (HMO / HMOPOS / Local PPO / …). */
  planType: string;
  offersPartD: boolean;
  /** Derived from `category` server-side, never stored. */
  includesPartD: boolean;
  /** Derived from `category` server-side; false for the drug-only categories. */
  includesMedical: boolean;
  /** ISO date, e.g. `2007-01-01`. */
  contractEffectiveDate: string;
  /** `null` = CMS-suppressed (≤10 enrollees). */
  enrollmentCount: number | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response of `GET /api/admin/plans/search`: one server-side page of the plans
 * catalogue. Query params: `page`, `page_size`, `search` (case-insensitive
 * substring over plan name, contract, PBP and insurer name), the exact filters
 * `category`, `insurer_id`, `plan_type` and `parent_organization`, and
 * `include_inactive`. Rows sort by (contract, PBP). `total` is the filtered
 * count, and an out-of-range `page` returns empty `items` with the correct
 * `total`.
 */
export interface PlanPage {
  items: Plan[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Response of `GET /api/admin/plans/filter-options`: the option lists for the
 * plans catalogue filters. Insurers referenced by at least one plan (sorted by
 * name), distinct non-null parent organizations, and distinct CMS plan types.
 * The options cover inactive plans too, so they don't change with
 * `include_inactive`.
 */
export interface PlanFilterOptions {
  insurers: PlanInsurerSummary[];
  parentOrganizations: string[];
  planTypes: string[];
}

/**
 * One selectable plan on the public enrollment form: a row of the
 * unauthenticated `GET /plans/available?state=XX`. Mirrors `PlanOption` in
 * server/app/models/api/plan.py.
 *
 * `state` is a 2-letter code (422 on an invalid one), and the response is `[]`
 * when no active Part D plan covers the state. Rows arrive filtered to the
 * state and sorted by (contract, PBP). `category` is a plain string on the
 * wire, with values from the `PlanCategory` taxonomy.
 */
export interface PlanOption {
  id: string;
  /** May be `""` (contract-level HCPP rows); the UI falls back to `insurerName`. */
  planName: string;
  insurerName: string | null;
  cmsContractNumber: string;
  cmsPlanId: string;
  category: string;
}

/**
 * Response of the public `GET /api/plans/years`: the plan years the plan lists
 * and estimates answer for. Mirrors `PlanYears` in
 * server/app/models/api/plan.py.
 *
 * `current` is this calendar year. `next` is the following contract year while
 * both of its datasets are ingested and the year has not started; null
 * otherwise, so a toggle offering it renders only while `next` is set.
 * `nextOopThreshold` is the statutory Part D out-of-pocket threshold for
 * `next`, null with it. Every year the UI shows comes from this payload, never
 * from a literal.
 */
export interface PlanYears {
  current: number;
  next: number | null;
  nextOopThreshold: number | null;
}

/**
 * Body of `PUT /api/admin/plans/{id}`, which carries only `notes`. Every other
 * plan field comes from the monthly ingest and is read-only on the wire. A
 * blank or whitespace-only value is stored as null.
 */
export interface PlanWrite {
  notes: string | null;
}

/**
 * Response of `GET /api/admin/plans/refresh-status`: the CMS monthly-refresh
 * status singleton. Mirrors `PlansRefreshStatus` in
 * server/app/models/api/plan.py.
 *
 * `state: "never_run"` is synthetic (all other fields null) when no refresh has
 * run. `POST /api/admin/plans/refresh` (super-admin only) returns 202 with an
 * optimistic `running` status, 409 while a refresh is running, and 503 when
 * the refresh task isn't configured or the launch fails. `counts` keys are the
 * snake_case ingest counters (`plans_created`, `plans_updated`,
 * `plans_deactivated`, `plans_reactivated`, `plans_unchanged`,
 * `insurers_created`, `review_flagged`). `nextYear` is the Landscape stage's
 * record, null until a run has looked for next year's file.
 */
export interface PlansRefreshStatus {
  state: "never_run" | "running" | "succeeded" | "failed";
  startedAt: string | null;
  finishedAt: string | null;
  /** Report month, e.g. `2026-07`. */
  reportPeriod: string | null;
  counts: Record<string, number> | null;
  error: string | null;
  violations: string[] | null;
  /** Email of the admin who triggered the run; null when no admin did. */
  triggeredBy: string | null;
  nextYear: PlansNextYearStatus | null;
}

/**
 * The plans refresh's Landscape stage record (`PlansRefreshStatus.nextYear`).
 * Mirrors `PlansNextYearStatus` in server/app/models/api/plan.py.
 *
 * `sourceFile` and `counts` describe a completed ingest of `contractYear`'s
 * Landscape, `error` a failed attempt, and `note` why a run had nothing to
 * ingest (no file published for the year).
 */
export interface PlansNextYearStatus {
  contractYear: number;
  sourceFile: string | null;
  finishedAt: string | null;
  counts: Record<string, number> | null;
  error: string | null;
  note: string | null;
}

/** One county in a plan's CMS service area. */
export interface ServiceAreaCounty {
  ssa: string;
  fips: string;
  county: string;
  partial: boolean;
  eghp: boolean;
}

/**
 * Response of `GET /api/admin/plans/{id}/service-area`. `locationAvailability`
 * is a state-code-keyed county map, the literal `"all"` for an entire-US
 * contract, or `null` when the contract has no service-area row.
 */
export interface PlanServiceArea {
  locationAvailability: Record<string, ServiceAreaCounty[]> | "all" | null;
}

/**
 * Full read projection of one catalogue insurer: the `GET`, `POST` and `PUT`
 * response of `/api/admin/insurers`. Mirrors `InsurerRead` in
 * server/app/models/api/plan.py.
 */
export interface InsurerRead {
  id: string;
  name: string;
  /** Stedi payer/trading-partner id for eligibility 270s; null until set. */
  stediId: string | null;
  logoUrl: string | null;
  website: string | null;
  paymentOptions: PaymentOption[];
  notes: string | null;
  /** CMS-sourced marketing name; read-only, null on manually created insurers. */
  insurerMarketingName: string | null;
  /** CMS-sourced parent organization; read-only, null on manually created insurers. */
  insurerParentOrganization: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response of `GET /api/admin/insurers/search`: one server-side page of the
 * insurers catalogue, in the same envelope as {@link PlanPage}. `search`
 * matches the insurer name only, not the marketing name, parent org or Stedi
 * ID, and rows sort by name.
 */
export interface InsurerPage {
  items: InsurerRead[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Body for `POST /api/admin/insurers` and `PUT /api/admin/insurers/{id}`
 * (full-object edit). `name` non-blank (unique case-insensitive among active
 * insurers, server-checked); `website` and portal payment values must start
 * `http(s)://`. `logoUrl` is the stored value unchanged, `null` to remove the
 * logo, or a staging link from the admin logo upload or fetch route, which the
 * server copies to the insurer's own logo; any other value 422s. Blank
 * optional fields as `null`; `paymentOptions` defaults `[]`.
 */
export interface InsurerWrite {
  name: string;
  stediId: string | null;
  logoUrl: string | null;
  website: string | null;
  paymentOptions: PaymentOption[];
  notes: string | null;
}

/** One field's old and new values inside an `update` audit entry's `diff`. */
export interface PlanFieldChange {
  old: unknown;
  new: unknown;
}

/**
 * One plan audit event (`GET /api/admin/plans/{id}/audit`, newest first).
 * `diff` is set only for `action: "update"` (`null` otherwise) and is keyed by
 * snake_case record field names (see naming note 2 above).
 */
export interface PlanAuditEntry {
  action: PlanAuditAction;
  at: string;
  actorEmail: string;
  actorName: string | null;
  diff: Record<string, PlanFieldChange> | null;
}

/**
 * One insurer audit event (`GET /api/admin/insurers/{id}/audit`, newest
 * first), the same shape as {@link PlanAuditEntry}. Its diff keys are
 * snake_case `InsurerRecord` field names, e.g. `stedi_id`.
 */
export type InsurerAuditEntry = PlanAuditEntry;

/**
 * One patient linked to a plan through an M3P assignment: half of the
 * `GET /api/admin/plans/{id}/linked` response. Mirrors `PlanLinkedPatient` in
 * server/app/models/api/plan.py.
 *
 * Deduped server-side, most recent enrollment first. The other half
 * (`enrollments`) reuses the admin `EnrollmentSummary` shape, so the combined
 * `PlanLinked` envelope lives in the admin app's `types/admin.ts`.
 */
export interface PlanLinkedPatient {
  patientId: string;
  fullName: string;
  dob: string; // YYYY-MM-DD
  phone: string;
  mbi: string;
  active: boolean;
}

/**
 * The runtime feature flags. Mirrors `FeatureFlags` in
 * server/app/models/api/feature_flags.py.
 *
 * Backs the super-admin-only `GET` and `PUT /api/admin/feature-flags`. The
 * `PUT` replaces the whole set, with no per-flag endpoint, so send every flag:
 * a field missing from a `PUT` resets that flag to `false`.
 */
export interface FeatureFlags {
  stediBypass: boolean;
  nppesBypass: boolean;
  /**
   * When true, the member and partner portal OTP flows skip the real send, and
   * every verify accepts the server's `BYPASS_OTP_CODE`. Inert in prod.
   */
  bypassSmsOtp: boolean;
  /**
   * Gates the Blue Button API (v3) connect: the enrollment flow's Medicare connect
   * (`/api/bluebutton/form-*`) and the portal connect endpoints. Both also
   * require Blue Button credentials, so the flag is inert without them.
   */
  bbMemberConnect: boolean;
  /**
   * Gates the portal LIS ("Extra Help") answer and reset endpoints, which
   * return 409 while it is off. `GET /api/portal/lis` reports the real status
   * either way, and the admin LIS routes never read the flag.
   */
  lisQuestionnaire: boolean;
  /**
   * When true, each successful `POST /api/enrollments` emails every
   * non-disabled admin. No environment gate applies.
   */
  newEnrollmentAdminEmail: boolean;
}

/**
 * Shape of a FastAPI error response body. The `ApiError` class exported from
 * `@dearbill/shared` is the runtime exception that wraps this payload.
 *
 * `detail` is usually a string, but some endpoints raise a structured detail.
 * `ApiError.detail` is `String(detail)`, so an object or list detail degrades
 * to a string such as `"[object Object]"`. The raw payload stays on
 * `ApiError.body` for callers that know the structured shape.
 */
export interface ApiErrorBody {
  detail: string | Record<string, unknown> | unknown[];
}

/**
 * Public "Contact us" wire shapes, mirrored from
 * server/app/models/api/contact.py. They back the unauthenticated
 * `ContactUsModal` that the customer, partner and member SPAs share.
 */

/** Which SPA a contact submission came from; it selects the staff-facing subject tag. */
export type ContactSource = "app" | "partner" | "member";

/**
 * Body of the public `POST /api/contact` (no auth). The server forbids extra
 * keys, so send exactly these. At least one of `email` and `phone` must be
 * present (422 otherwise). `website` is a honeypot: render it hidden and
 * empty; a non-empty value is accepted silently without sending. The server
 * model caps the lengths of the required `topic` and `message`. The response
 * is `202` with an empty body.
 */
export interface ContactCreate {
  source: ContactSource;
  email?: string | null;
  phone?: string | null;
  topic: string;
  message: string;
  /** Honeypot; always empty for real users. */
  website?: string | null;
}

/**
 * Response of the public `GET /api/contact/support-line` (no auth). Mirrors
 * `SupportLine` in server/app/models/api/contact.py. `supportLineNumber` is the
 * support line (the IVR's DID) in E.164, or null when the server's
 * `TELNYX_SUPPORT_LINE_NUMBER` is blank; callers then show no number.
 */
export interface SupportLine {
  supportLineNumber: string | null;
}

/**
 * Request-a-call wire shapes, mirrored from
 * server/app/models/api/call_requests.py. The customer, member and partner
 * SPAs POST these through the shared `ContactUsModal`.
 */

/** Which SPA a call request came from. */
export type CallRequestSource = "app" | "member" | "partner";

/** ET calling window the caller picks. The display labels live in the UI. */
export type CallRequestTimeWindow = "morning" | "afternoon" | "late_afternoon";

/** Which public form produced a call-request row. */
export type CallRequestKind = "call" | "plan_mismatch";

/** A box the plan-confirmation step's "What doesn't look right?" form offers. */
export type PlanMismatchReason = "plan_name" | "my_name" | "coverage_dates" | "other";

/** Time of day a plan-mismatch submitter prefers for the callback. */
export type PlanMismatchTimeWindow = "morning" | "afternoon" | "evening";

/**
 * Body of the public `POST /api/contact/plan-mismatch` (no auth), sent from
 * the plan-confirmation step when the Medicare plan data looks wrong. Mirrors
 * `PlanMismatchCreate` in server/app/models/api/call_requests.py. The names
 * come from the Blue Button prefill and are null when it withheld them. The
 * server normalises `phone` to E.164 under the `CallRequestCreate` rule and
 * stamps `source: "app"`. `reasons` holds the checked boxes and may be empty;
 * `reasonText` is required when `reasons` includes `"other"`. Render `website`
 * hidden and empty. Success is `201` with an empty body; `422` is a validation
 * error and `429` the per-phone rate limit, which shares the call-request
 * budget (`MAX_CALL_REQUESTS` per `CALL_REQUEST_WINDOW_SECONDS`).
 */
export interface PlanMismatchCreate {
  firstName: string | null;
  lastName: string | null;
  phone: string;
  email: string;
  timeWindow: PlanMismatchTimeWindow;
  reasons: PlanMismatchReason[];
  reasonText: string | null;
  /** Honeypot; always empty for real users. */
  website?: string | null;
}

/**
 * Body of the public `POST /api/contact/call-requests` (no auth). Every field
 * except the `website` honeypot is required. `requestedDate` is `YYYY-MM-DD`,
 * a weekday from tomorrow to the server's `_MAX_DAYS_AHEAD` days out,
 * evaluated in America/New_York. Render `website` hidden and empty. Success is
 * `201` with an empty body; `422` is a validation error and `429` the
 * per-phone rate limit (`MAX_CALL_REQUESTS` per `CALL_REQUEST_WINDOW_SECONDS`).
 */
export interface CallRequestCreate {
  source: CallRequestSource;
  firstName: string;
  lastName: string;
  phone: string;
  zip: string;
  requestedDate: string;
  timeWindow: CallRequestTimeWindow;
  /** Honeypot; always empty for real users. */
  website?: string | null;
}

/** Kind of known entity a call-request phone number resolved to. */
export type MatchedContactType = "patient" | "caregiver" | "partner" | "organization" | "insurer";

/**
 * The known entity a call request's phone number matched server-side, so the
 * admin sees who is calling. `entityId` is null for a match with no admin
 * detail route, such as a partner user. Mirrors `MatchedContact` in
 * server/app/models/api/call_requests.py.
 */
export interface MatchedContact {
  name: string;
  type: MatchedContactType;
  entityId: string | null;
}

/**
 * One row of `GET /api/admin/call-requests` (admin Bearer auth), newest first.
 *
 * A `"call"` row is a `CallRequestCreate`; a `"plan_mismatch"` row is a
 * `PlanMismatchCreate`, on which the names may be `""`, `zip` and
 * `requestedDate` are null, and `timeWindow` is a `PlanMismatchTimeWindow`.
 * `email`, `reasons` and `reasonText` carry a value only on plan-mismatch rows.
 */
export interface CallRequestRead {
  id: string;
  createdAt: string;
  source: CallRequestSource;
  kind: CallRequestKind;
  firstName: string;
  lastName: string;
  /** E.164. */
  phone: string;
  zip: string | null;
  requestedDate: string | null;
  timeWindow: CallRequestTimeWindow | PlanMismatchTimeWindow;
  email: string | null;
  reasons: PlanMismatchReason[];
  reasonText: string | null;
  /** Present when the phone number resolved to a known entity; else null. */
  matchedContact?: MatchedContact | null;
}

/**
 * The cursor-paginated envelope of `GET /api/admin/call-requests`. Mirrors
 * `Page` in server/app/models/api/pagination.py.
 *
 * Returned only when the request sets `paginate=true` or carries a `cursor`;
 * otherwise the endpoint returns the plain `CallRequestRead[]`. `q` searches
 * name and phone server-side, and `source` filters exactly. `nextCursor` is an
 * opaque forward-only token, null exactly when the list is exhausted. Page one
 * has no cursor, and Previous uses the client-side cursor stack
 * (`useCursorPager`). A malformed cursor, or one replayed under different
 * filters, is a 422 `{"detail": "invalid cursor"}`: restart from page one
 * rather than show an error. `total` is counter-backed on the default view and
 * counts filtered rows on the search and filter scan paths.
 */
export interface CallRequestPage {
  items: CallRequestRead[];
  total: number;
  pageSize: number;
  nextCursor: string | null;
}

/**
 * Scheduled-call booking wire shapes, mirrored from
 * server/app/models/api/scheduled_calls.py. The public book-a-call page POSTs
 * these; the admin list and patch shapes are admin-only and live in
 * frontend/admin/src/types/admin.ts.
 */

/** Which surface booked the call; the public SPAs plus an admin booking on a patient's behalf. */
export type ScheduledCallSource = "app" | "member" | "partner" | "admin";

/** ET calling window the caller picks, the request-a-call set. */
export type ScheduledCallTimeWindow = CallRequestTimeWindow;

/**
 * Why the call exists; the voice agent selects its script from it. Mirrors
 * `ScheduledCallPurpose` in server/app/models/db/scheduled_call.py. The
 * public booking accepts only `"m3p"` and `"screening"` (any other value is a
 * 422) and defaults to `"m3p"` when absent; the admin PATCH may set any
 * value.
 */
export type ScheduledCallPurpose = "m3p" | "authorization" | "screening" | "welcome" | "status";

/**
 * Body of the public `POST /api/scheduled-calls` (no auth). Mirrors
 * `ScheduledCallCreate` in server/app/models/api/scheduled_calls.py.
 * `requestedDate` is `YYYY-MM-DD`, a weekday from tomorrow to 30 days out,
 * evaluated in America/New_York (the request-a-call rule). `email` is
 * optional; empty becomes null. Render `website` hidden and empty. Success is
 * `201` with a `ScheduledCallCreated` body; `422` is a validation error and
 * `429` the per-phone rate limit.
 */
export interface ScheduledCallCreate {
  source: ScheduledCallSource;
  /** Optional; only `"m3p"` and `"screening"` pass the public route. Absent books an m3p call. */
  purpose?: ScheduledCallPurpose;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string | null;
  zip: string;
  requestedDate: string;
  timeWindow: ScheduledCallTimeWindow;
  /** Honeypot; always empty for real users. */
  website?: string | null;
}

/**
 * Response of `POST /api/scheduled-calls`. Mirrors `ScheduledCallCreated` in
 * server/app/models/api/scheduled_calls.py. `scheduledFor` is the stored UTC
 * instant; the SPA renders the booked date and window itself.
 */
export interface ScheduledCallCreated {
  id: string;
  scheduledFor: string;
  timeWindow: ScheduledCallTimeWindow;
}

/**
 * Response of `GET /api/portal/me` (portal session bearer; 401 without).
 * Mirrors `PortalMe` in server/app/models/api/portal.py. The member SPA
 * prefills its contact modal from it. On a caregiver session the contact
 * fields are the caregiver's own. Every field is always present; `email` is
 * null when none is on file.
 */
export interface PortalMe {
  firstName: string;
  lastName: string;
  phone: string;
  email: string | null;
  /**
   * The patient's notification preferences; null on a caregiver session (they
   * are the patient's, not the caregiver's) and when the session patient no
   * longer resolves.
   */
  preferences: PortalPreferences | null;
}

/**
 * The patient's notification preferences, carried by `PortalMe` and returned by
 * `PATCH /api/portal/preferences`. Mirrors `PortalPreferences` in
 * server/app/models/api/portal.py.
 */
export interface PortalPreferences {
  /** The patient row's `email_notifications` flag, which gates every automated patient email. */
  emailNotifications: boolean;
  /** True only while the mobile number's SMS-consent row is `opted_in`. */
  smsNotifications: boolean;
  /**
   * True while the mobile's row is an SMS-sourced `opted_out` (the patient
   * texted STOP). Only a texted START lifts it, so the portal cannot turn texts
   * back on and the server answers `smsNotifications: true` with 409.
   */
  smsOptedOutByText: boolean;
}

/**
 * Body of `PATCH /api/portal/preferences` (portal session bearer, members
 * only). Mirrors `PortalPreferencesPatch` in server/app/models/api/portal.py:
 * unknown keys are rejected and at least one key is required (422 otherwise).
 * Only the channels present are written. Returns the full
 * `PortalPreferences`; 401 without a live session, 403 on a caregiver session,
 * 404 for a missing or deactivated patient, 409 on `smsNotifications: true`
 * while `smsOptedOutByText`.
 */
export interface PortalPreferencesPatch {
  emailNotifications?: boolean;
  smsNotifications?: boolean;
}

/**
 * Medicare eligibility (Stedi 270/271) wire shapes, mirrored from the
 * `MedicareCheckResult` tree in server/app/models/api/admin.py, which in turn
 * mirrors the `app.eligibility` facts DTO. The server names the sub-models with
 * a `Medicare` prefix (`MedicareDateWindow`, `MedicareContact`, …). They back
 * `POST /api/admin/enrollments/{id}/medicare-eligibility` and
 * `EnrollmentReadAdmin.medicareCheck`.
 *
 * These shapes carry facts, never decisions: `statedStatus` is as Medicare
 * states it, and date windows, `deceasedOn` and `demographics.dateOfBirth` are
 * raw `YYYYMMDD` strings, not ISO. `checkedAt` is a UTC ISO 8601 timestamp.
 */

/** A coverage date window, carried raw as `YYYYMMDD` (never evaluated). */
export interface DateWindow {
  start: string | null;
  end: string | null;
}

/** Plan/payer contact details, if surfaced by Medicare. */
export interface Contact {
  name: string | null;
  phone: string | null;
  url: string | null;
  address: string | null;
}

/** A Part A or Part B entitlement line, with the status as Medicare states it. */
export interface CoverageFact {
  part: "A" | "B" | null;
  statedStatus: "active" | "inactive" | null;
  window: DateWindow | null;
  sourceIndex: number;
}

/** A drug-plan enrollment line (PDP or MA-PD), identity only. */
export interface DrugPlanFact {
  kind: "pdp" | "mapd";
  contractId: string | null;
  planName: string | null;
  pbm: string | null;
  contact: Contact | null;
  window: DateWindow | null;
  sourceIndex: number;
}

/** A payer/AAA signal carried as data (not raised): subscriber not found, mismatch, … */
export interface ResponseSignal {
  kind:
    | "subscriber_not_found"
    | "name_mismatch"
    | "member_id_invalid"
    | "dob_mismatch"
    | "invalid_provider"
    | "other";
  field: string | null;
  rawCode: string;
  message: string | null;
}

/** Medicare's on-file demographics for the member (the "known info"). `dateOfBirth` is `YYYYMMDD`; `address` is a single line. */
export interface Demographics {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  suffix: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  address: string | null;
}

/** Surrogate identifiers describing where the response came from (audit/trace only). */
export interface Provenance {
  vendor: string;
  checkId: string | null;
}

/** The member the response is about. */
export interface MedicareMemberRef {
  memberId: string;
}

/**
 * Ops guidance for the eligibility scenario a check classifies into. Mirrors
 * `ScenarioInfo` in server/app/models/api/admin.py.
 *
 * The server classifies real and synthetic results the same way.
 * `MedicareCheckResult.scenario` is null when the scenario id is unknown; on
 * the synthetic FR-0.3 error path the block is at `err.body.detail.scenario`.
 */
export interface ScenarioInfo {
  id: string; // e.g. "FR-2.1"
  layer: number; // 0–4
  title: string;
  outcome: string; // e.g. "Proceed (Golden path)"
  signal: string; // free text; may be ""
  systemBehavior: string;
  opsAction: string;
  memberFacing: string;
  dataCaptured: string;
}

/**
 * The latest Medicare eligibility check for an enrollment and when it ran: the
 * value of `EnrollmentReadAdmin.medicareCheck` and the response body of
 * `POST /api/admin/enrollments/{id}/medicare-eligibility`.
 */
export interface MedicareCheckResult {
  member: MedicareMemberRef;
  coverages: CoverageFact[];
  drugPlans: DrugPlanFact[];
  deceasedOn: string | null; // YYYYMMDD
  responseSignals: ResponseSignal[];
  demographics: Demographics | null;
  provenance: Provenance;
  checkedAt: string; // UTC ISO 8601
  scenario?: ScenarioInfo | null; // Ops guidance; null or absent when the scenario id is unknown
}

/**
 * Response body of the ad-hoc `POST /api/admin/medicare-eligibility` only; the
 * enrollment-tied route and `EnrollmentReadAdmin.medicareCheck` use the
 * raw-less base. Mirrors `MedicareCheckResultWithRaw` in
 * server/app/models/api/admin.py.
 */
export interface MedicareCheckResultWithRaw extends MedicareCheckResult {
  /** Verbatim Stedi 271 body; never persisted. */
  raw: Record<string, unknown> | null;
}

/**
 * M3P plan-enrollment lifecycle, mirrored from `M3pEnrollmentRead` in
 * server/app/models/api/admin.py and the `M3p*` literals in
 * server/app/models/db/enrollment.py. `effectiveDate` is `YYYY-MM-DD`, while
 * `submittedAt`, `confirmationReceivedAt` and `at` are UTC ISO 8601.
 */
export type M3pStatus =
  | "not_yet_started"
  | "in_progress"
  | "submitted"
  | "confirmed"
  | "active"
  | "terminated"
  | "failed";

export type M3pSubmissionChannel =
  | "email"
  | "mail"
  | "phone_3way"
  | "phone_member_alone"
  | "online_ops_assisted"
  | "online_member_self"
  | "fax"
  | "other";

export type M3pConfirmationMethod =
  | "plan_mail"
  | "plan_phone"
  | "plan_email"
  | "member_report"
  | "other";

export type M3pTerminationReason =
  | "member_opt_out"
  | "member_disenrolled_from_plan"
  | "lis_qualification"
  | "other";

/** One M3P lifecycle event, stored in append order (oldest first). */
export interface M3pHistoryEntry {
  at: string; // UTC ISO 8601
  actorEmail: string;
  actorName: string | null;
  event: "assigned" | "plan_changed" | "status_changed";
  status: M3pStatus;
  note: string | null;
}

/**
 * The assigned M3P plan and its lifecycle state. `null` on
 * `EnrollmentReadAdmin.m3pEnrollment` until an admin assigns a plan; deleting
 * the assignment removes the whole object.
 */
export interface M3pEnrollment {
  planId: string;
  status: M3pStatus;
  submissionChannel: M3pSubmissionChannel | null;
  submittedAt: string | null;
  confirmationReceivedAt: string | null;
  confirmationMethod: M3pConfirmationMethod | null;
  confirmationReference: string | null;
  effectiveDate: string | null; // YYYY-MM-DD
  terminationReason: M3pTerminationReason | null;
  history: M3pHistoryEntry[];
}

/**
 * Body of the ad-hoc `POST /api/admin/medicare-eligibility`, which checks a
 * patient the admin keys in, with no enrollment row. Mirrors
 * `MedicareCheckRequest` in server/app/models/api/admin.py. Responds with
 * `MedicareCheckResultWithRaw`.
 *
 * `mbi`, `payerId` (the plan or payer the 270 is addressed to), `providerNpi`
 * and `providerOrgName` (the provider identity the 270 runs under; a blank
 * value is a 422) are required. Optional fields are omitted when not
 * provided, never sent as empty strings. `dob` is ISO `YYYY-MM-DD`, a form
 * value, unlike the raw `YYYYMMDD` facts in the result.
 */
export interface MedicareCheckRequest {
  mbi: string;
  payerId: string;
  providerNpi: string;
  providerOrgName: string;
  firstName?: string;
  lastName?: string;
  dob?: string; // YYYY-MM-DD
}

/**
 * Call API shapes, mirrored from server/app/models/api/calls.py.
 *
 * `CallOutcome` mirrors the `CallOutcome` literal in
 * server/app/models/db/call.py. `voicemail` is an inbound support-line call
 * that left a recording.
 */
export type CallOutcome = "completed" | "no_answer" | "busy" | "failed" | "canceled" | "voicemail";

/**
 * A dialable target on an enrollment, resolved server-side to a phone number
 * and a `CalleeType`. Free-form numbers don't use a `CallTarget`; they go
 * through the `number` field of `CallCredentialsRequest` or
 * `AddParticipantRequest`.
 */
export type CallTarget = "plan" | "patient_mobile" | "patient_home" | "caregiver" | "pharmacy";

/**
 * Type of a called party. Mirrors the `CalleeType` literal in
 * server/app/models/db/call.py. The `plan` target maps to `health_insurer`,
 * both patient phones to `patient`, and every other target to the same-named
 * type. A free-form (manually keyed) number is `other`. No target maps to
 * `referring_provider`; stored call participants and speech segments carry it.
 */
export type CalleeType =
  | "health_insurer"
  | "patient"
  | "caregiver"
  | "pharmacy"
  | "referring_provider"
  | "other";

/**
 * One participant on a conference call. Mirrors `ParticipantApi` in
 * server/app/models/api/calls.py.
 *
 * On an outbound call the admin holds one WebRTC leg (`role: "admin"`), and the
 * server dials every other party as its own PSTN leg (`role: "callee"`).
 * `participantId` is a stable slug (e.g. `admin`, `p1`, `p2`) that keys the
 * persisted transcript `speaker`, the DTMF echo `speaker`, and the
 * per-participant control endpoints.
 *
 * `status` moves from `pending` (dial queued) through `ringing` to
 * `connected`, and ends as `left` (hung up or dropped) or `failed` (couldn't
 * connect). The admin leg is `connected` once its WebRTC leg is up.
 */
export interface ParticipantApi {
  participantId: string;
  role: "admin" | "callee";
  /** Display name: the dialed party's, or on the admin leg the admin's Google name, else email. */
  name: string;
  /** Callee type for `role: "callee"`; null for the admin. */
  type: CalleeType | null;
  /**
   * E.164 number dialed; null for the admin's own WebRTC leg. The support agent
   * dialed into an inbound call is admin-role but carries its number.
   */
  phoneE164: string | null;
  status: "pending" | "ringing" | "connected" | "left" | "failed";
  /** ISO 8601 UTC when the leg connected; null until then. */
  joinedAt: string | null;
  /** ISO 8601 UTC when the leg left/failed; null while live. */
  leftAt: string | null;
}

/**
 * Request body for `POST /api/admin/calls/credentials`. Mirrors
 * `CallCredentialsRequest` in server/app/models/api/calls.py.
 *
 * Seeds the conference's first party as either a known enrollment `target` or
 * a free-form E.164 `number`. Send exactly one; both or neither is a 422.
 * `label` is an optional display name for the free-form case (the number by
 * default).
 *
 * `enrollmentId` is optional: the global call modal can dial a free-form
 * `number` before any enrollment is chosen and assign the call later through
 * `PATCH /api/admin/calls/{callId}/assignment`. A `target` seed requires it,
 * since there is no enrollment to resolve the target against otherwise (422).
 */
export interface CallCredentialsRequest {
  enrollmentId?: string | null;
  target?: CallTarget;
  number?: string;
  label?: string;
}

export interface CallCredentialsResponse {
  callId: string;
  /** Short-lived Telnyx WebRTC JWT; pass it as `login_token` to TelnyxRTC. */
  credentialToken: string;
  /** The credential's SIP username, optional as `login` on `TelnyxRTC`. */
  sipUsername: string;
  /** E.164; pass it as `callerNumber` on `client.newCall(...)`. */
  callerNumber: string;
  /**
   * Sentinel destination the browser dials to seed the conference. The server
   * intercepts this leg as the admin leg, creates the conference, and dials
   * every party itself, the first included; the browser dials nothing else.
   * Pass as `destinationNumber` on `client.newCall(...)`.
   */
  dialTarget: string;
  /** Echo of the request's enrollment link; null for an unassigned call. */
  enrollmentId: string | null;
  /**
   * Base64-encoded blob the SDK forwards to Telnyx on dial. Telnyx echoes it on
   * every webhook for the call, so the dispatcher finds the right `CallRecord`
   * in O(1). It encodes the call id and the admin participant; the frontend
   * only passes it through on the WebRTC dial.
   */
  clientState: string;
  /**
   * The participants planned at mint time: the admin leg plus the first dialed
   * party. The modal seeds its columns from this and reconciles them from the
   * call-row poll (`useCallPoll`, `GET /api/admin/calls/{callId}`) as more
   * parties join.
   */
  participants: ParticipantApi[];
}

/**
 * Request body for `POST /api/admin/calls/{callId}/participants` (Add to Call).
 * Mirrors `AddParticipantRequest` in server/app/models/api/calls.py. Send
 * exactly one of `target` (a known enrollment number) or `number` (a free-form
 * E.164 the server validates). `label` is an optional display name for the
 * free-form case (the number by default; the admin picker always sends one).
 * Responds 201 with the new `ParticipantApi`.
 */
export interface AddParticipantRequest {
  target?: CallTarget;
  number?: string;
  label?: string;
}

/**
 * Request body for `PATCH /api/admin/calls/{callId}/assignment`. Mirrors
 * `CallAssignmentRequest` in server/app/models/api/calls.py. `enrollmentId` is
 * required: there is no un-assign, so a set assignment can't be cleared.
 */
export interface CallAssignmentRequest {
  enrollmentId: string;
}

/**
 * Response from `PATCH /api/admin/calls/{callId}/assignment`. Echoes the
 * stamped link so the modal can update its header without re-polling the
 * call detail.
 */
export interface CallAssignmentResponse {
  callId: string;
  enrollmentId: string;
  enrollmentFullName: string | null;
}

export interface CallSummary {
  callId: string;
  /**
   * Null until the call is assigned to an enrollment, which can happen during
   * or after the call.
   */
  enrollmentId: string | null;
  /**
   * Denormalised at credential mint and assignment; null when the name is unset
   * or the call is unassigned.
   */
  enrollmentFullName: string | null;
  /**
   * The owning admin. Null only on inbound support-line rows, whose `adminName`
   * is a fixed support-line label.
   */
  adminEmail: string | null;
  adminName: string | null;
  /**
   * `inbound` is a press-1 or press-2 call to the support line, with one callee
   * participant, the caller; every admin-dialed call is `outbound`.
   */
  direction: "outbound" | "inbound";
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  outcome: CallOutcome | null;
  recordingS3Key: string | null;
  /**
   * Free-text admin notes typed into the call modal during (or after)
   * the call. Saved via `PATCH /api/admin/calls/{callId}/notes` with a
   * debounced autosave. Null when the admin hasn't typed anything.
   */
  notes: string | null;
  /**
   * Every participant on the call: the admin leg plus each dialed party, or the
   * inbound caller. Drives the calls-list "Parties" column and the replay
   * page's per-participant transcript labels. Empty `[]` when the stored row
   * has none.
   */
  participants: ParticipantApi[];
  /** Soft-delete flag; false once the call is deactivated. */
  active: boolean;
}

/**
 * The cursor-paginated envelope of `GET /api/admin/calls`. Mirrors `Page` in
 * server/app/models/api/pagination.py.
 *
 * Returned only when the request sets `paginate=true` or carries a `cursor`;
 * otherwise, as for FormDetail's `?enrollment_id=` fetch, the endpoint returns
 * the plain `CallSummary[]`. `q` searches name, parties and call id
 * server-side; `unassigned_only=true` and `include_inactive` filter.
 * `nextCursor` is an opaque forward-only token, null exactly when the list is
 * exhausted. Page one has no cursor, and Previous uses the client-side cursor
 * stack (`useCursorPager`). A malformed cursor, or one replayed under
 * different filters, is a 422 `{"detail": "invalid cursor"}`: restart from
 * page one rather than show an error. `total` is counter-backed on the default
 * view and counts filtered rows on the search and filter scan paths.
 */
export interface CallPage {
  items: CallSummary[];
  total: number;
  pageSize: number;
  nextCursor: string | null;
}

/**
 * A call audit event's action. Mirrors the `CallAuditAction` literal in
 * server/app/models/db/call.py. As for patients and enrollments, the
 * soft-delete is reversible, so `reactivate` exists.
 */
export type CallAuditAction = "create" | "update" | "assign" | "deactivate" | "reactivate";

/** One field's old and new values inside an `update` audit entry's `diff`. */
export interface CallFieldChange {
  old: unknown;
  new: unknown;
}

/**
 * One call audit event (`GET /api/admin/calls/{callId}/audit`, newest first).
 * Mirrors `CallAuditEntry` in server/app/models/api/calls.py, the same shape as
 * `PlanAuditEntry`. `update` events (notes edits) carry a
 * `{ notes: { old, new } }` diff, and `assign` events the old and new
 * enrollment. Lifecycle events (create, deactivate, reactivate) carry
 * `diff: null`.
 */
export interface CallAuditEntry {
  action: CallAuditAction;
  at: string;
  actorEmail: string;
  actorName: string | null;
  diff: Record<string, CallFieldChange> | null;
}

/**
 * Persisted transcript wire shapes, mirrored from
 * server/app/models/api/calls.py. Speech and DTMF entries share one list,
 * discriminated by `kind`, so the replay UI renders them inline in
 * chronological order.
 */
export interface SpeechSegmentApi {
  kind: "speech";
  seq: number;
  /** Participant slug (`participantId`) of the speaker (e.g. `admin`, `p1`). */
  speaker: string;
  text: string;
  isFinal: boolean;
  // Wall-clock epoch ms (the leg's `recording_started_at` plus the utterance
  // offset). The replay UI aligns segments onto the recording through
  // `recordingStartedAtMs` (`offsetFor.ts`).
  startedAtMs: number;
  endedAtMs: number;
  // Callee type of the speaker, stamped when the leg's transcription is
  // written, from the participant keyed on `speaker`. Null for the admin leg
  // and any unresolved speaker. The replay renderer labels bubbles from
  // `summary.participants` (matched by `speaker`), not from this field.
  type: CalleeType | null;
}

export interface DtmfSegmentApi {
  kind: "dtmf";
  digit: string;
  startedAtMs: number;
  speaker: string;
}

export type TranscriptSegmentApi = SpeechSegmentApi | DtmfSegmentApi;

/**
 * Response of `GET /api/admin/calls/{callId}`. `recordingUrl` is a short-lived
 * presigned S3 GET, null until both the processed recording and the sync
 * anchor `recordingStartedAtMs` exist. The server computes `recordingPending`
 * and `transcriptPending` from the media it actually started, so the replay
 * page shows a processing state only while started media is on its way. A row
 * without start markers returns both false.
 */
export interface CallDetail {
  summary: CallSummary;
  transcript: TranscriptSegmentApi[];
  recordingUrl: string | null;
  recordingStartedAtMs: number | null;
  /**
   * The server started the replay (mixed) recording and its S3 asset hasn't
   * landed yet. False when no replay recording was ever started (e.g. a call
   * canceled before it connected) and once the asset has landed.
   */
  recordingPending: boolean;
  /**
   * At least one per-leg recording was started whose post-call transcription
   * hasn't finished yet (a failed transcription counts as finished). False
   * when no leg recording was started.
   */
  transcriptPending: boolean;
}

/**
 * Request body for `POST /api/admin/calls/{callId}/dtmf`. The backend
 * validates `digit`, so the client sends the pressed key as-is.
 */
export interface CallDtmfRequest {
  digit: string;
}

/**
 * Response from `POST /api/admin/calls/{callId}/dtmf`. Mirrors
 * `CallDtmfResponse` in server/app/models/api/calls.py. The call modal paints
 * the keypad bubble from this echo; the call-row poll re-delivers the same
 * entry inside `transcript`, and the client merge de-dups on `startedAtMs`.
 * `speaker` is the admin participant's `participantId`, since DTMF rides the
 * admin leg.
 */
export interface CallDtmfResponse {
  digit: string;
  startedAtMs: number;
  speaker: string;
}

/**
 * Request body for `PATCH /api/admin/calls/{callId}/notes`. An empty string or
 * null clears the field (the backend stores no notes as an absent attribute).
 * The server caps the length, and the call modal's textarea enforces the same
 * `MAX_LEN`.
 */
export interface CallNotesRequest {
  notes: string | null;
}

/**
 * Response from `PATCH /api/admin/calls/{callId}/notes`. The frontend reads
 * only success or failure; the textarea stays the source of truth.
 */
export interface CallNotesResponse {
  callId: string;
  notes: string | null;
}

/**
 * Member portal wire shapes, mirrored from server/app/models/api/portal.py.
 *
 * Auth is a self-issued portal-session bearer, not OIDC, carried as
 * `Authorization: Bearer <session>` on every other portal call. Sign-in is
 * OTP-gated:
 *   1. `POST /api/portal/login` matches the phone plus one identity-factor
 *      group (see `PortalLoginRequest`) and returns a short-lived `challenge`,
 *      not a session. Two ambiguous outcomes return 200 with `challenge: null`
 *      instead; see `PortalLoginResponse`. Every identity mismatch is the same
 *      opaque 401, and too many attempts are a 429.
 *   2. `POST /api/portal/login/resend` sends the OTP on the chosen channel.
 *   3. `POST /api/portal/login/verify` exchanges `{ challenge, code }` for the
 *      `session`.
 * The only OTP-less mint is `POST /api/portal/login/handoff`, which exchanges
 * the single-use `EnrollmentRead.portalHandoffToken` (see
 * `PortalLoginHandoffRequest`).
 */
export interface PortalLoginRequest {
  /**
   * Who is signing in: `member` is the patient, and `caregiver` an authorized
   * representative. Only the meaning of `phone` differs (the member's own or
   * the caregiver's); `mbi`, `lastName`, `dob` and `zip` always refer to the
   * patient.
   */
  role: "member" | "caregiver";
  phone: string;
  /**
   * Identity factors, in two alternative groups; all four default to `""`
   * server-side. The server requires either a non-blank `mbi` or all three of
   * `lastName`, `dob` and `zip` (422 otherwise), and every provided factor must
   * match the patient (opaque 401 otherwise), so the client sends only the
   * active group.
   */
  /** The patient's Medicare id (MBI), normalised and matched server-side. */
  mbi?: string;
  lastName?: string;
  /** `YYYY-MM-DD`. */
  dob?: string;
  zip?: string;
}

export interface PortalLoginResponse {
  /**
   * Short-lived challenge token returned on a successful identity match. It is
   * not a session bearer: exchange it with the OTP at
   * `POST /api/portal/login/verify` for the `session`. `null` on the ambiguous
   * outcomes (`mbiRequired`, `identityAmbiguous`).
   */
  challenge: string | null;
  /**
   * `true` only when the SMS-OTP bypass is in effect, which the server never
   * allows in prod: no code is sent, and verify accepts `BYPASS_OTP_CODE`.
   * Shows the shared `BypassOtpBanner` on the OTP step; a missing value hides
   * it, like `false`.
   */
  bypassSmsOtp: boolean;
  /**
   * `true` when the same personal details match several patients with
   * different MBIs, so no `challenge` is issued; re-submit with `mbi` to
   * disambiguate. Applies to both roles.
   */
  mbiRequired?: boolean;
  /**
   * `true` when the submitted factors, including `mbi` when provided, match
   * more than one patient record and the server can't tell them apart; no
   * `challenge` is issued. Unlike `mbiRequired` this is terminal: more factors
   * won't help, and the member must contact support. Applies to both roles.
   */
  identityAmbiguous?: boolean;
}

/**
 * Body of `POST /api/portal/login/verify`: a `challenge` from the login step
 * plus the OTP `code`, exchanged for a portal session.
 */
export interface PortalLoginVerifyRequest {
  challenge: string;
  code: string;
}

/**
 * Response of `POST /api/portal/login/verify`, always `200` for an accepted,
 * rejected, expired or exhausted code. A bad or expired challenge token is a
 * `401`, and a Telnyx error a `502`. On success `session` is the bearer token
 * and `reason` is null; otherwise `session` is null and `reason` says why
 * (`"rejected"` wrong code, `"expired"` code lapsed, `"max_attempts_exceeded"`
 * too many wrong codes).
 */
export interface PortalLoginVerifyResponse {
  session: string | null;
  reason: "rejected" | "expired" | "max_attempts_exceeded" | null;
}

/**
 * Body of `POST /api/portal/login/handoff`, the only OTP-less session mint. It
 * exchanges the single-use `EnrollmentRead.portalHandoffToken`, carried from
 * the enrollment flow's Done screen in the URL fragment, for a portal session.
 * Public (no bearer). Mirrors `PortalLoginHandoffRequest` in
 * server/app/models/api/portal.py.
 */
export interface PortalLoginHandoffRequest {
  handoff: string;
}

/**
 * Response of `POST /api/portal/login/handoff`: `200` with the bearer
 * `session`. Every failure (expired, already used, tampered) is the same
 * opaque `401`, never a 200 with a reason.
 */
export interface PortalLoginHandoffResponse {
  session: string;
}

/**
 * OTP delivery channel for every code-send endpoint (portal login, partner
 * login 2FA, partner signup phone verify). `"sms"` is a
 * Telnyx Verify text message and `"call"` a Telnyx Verify voice call that reads
 * the code aloud. The server default is `"sms"`. Mirrors `OtpChannel` in
 * server/app/models/api/verify.py.
 */
export type OtpChannel = "sms" | "call";

/**
 * Body of `POST /api/portal/login/resend`, the only send path for the login
 * OTP: it makes the first send and every resend, because `POST /portal/login`
 * mints the challenge without sending a code. `channel` picks the delivery
 * method (default `"sms"`). Returns `200 {"status":"pending"}`; `429` within
 * the per-phone `SEND_COOLDOWN_SECONDS` window, which both channels share;
 * `401` for a bad or expired challenge; `502` when the send fails.
 */
export interface PortalLoginResendRequest {
  challenge: string;
  channel?: OtpChannel;
}

/**
 * Malware-scan verdict for an uploaded supporting document. Every upload
 * stamps a status. With scanning disabled every upload is `"clean"`; the other
 * states occur only while scanning is enabled.
 */
export type ScanStatus = "pending" | "clean" | "infected" | "error";

/**
 * One uploaded supporting document (pharmacy receipt or payment bill). `url` is
 * a presigned S3 GET the browser can open directly in a new tab, null until the
 * file passes the malware scan: only `"clean"` files get a URL.
 */
export interface PortalAttachment {
  filename: string;
  contentType: string;
  /** Bytes. */
  size: number;
  /** ISO 8601 UTC. */
  uploadedAt: string;
  /** Presigned GET URL; null whenever `scanStatus` is not `"clean"`. */
  url: string | null;
  /** Malware-scan verdict; only `"clean"` files carry a `url`. */
  scanStatus: ScanStatus;
}

/** Row shape for `GET /api/portal/enrollments` (newest first). */
export interface PortalEnrollmentSummary {
  id: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  status: EnrollmentStatus;
  firstName: string;
  lastName: string;
  esignStatus: EsignStatus;
}

/**
 * Full detail for `GET /api/portal/enrollments/{id}`, and the response of both
 * upload endpoints, which return the updated detail.
 */
export interface PortalEnrollmentDetail {
  id: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  status: EnrollmentStatus;
  firstName: string;
  lastName: string;
  /** The member's Medicare Beneficiary Identifier; "" (never null) when unset. */
  mbi: string;
  esignStatus: EsignStatus;
  /**
   * True when the form is complete enough to sign and not yet signed; the
   * member's in-dashboard sign view opens only while it is true.
   */
  signable: boolean;
  /**
   * The name the sign view prefills: the participant's full name, or the
   * caregiver's when an authorized representative signs.
   */
  signerName: string;
  signedAs: "patient" | "caregiver";
  /** ISO 8601 UTC when the form was signed; null until signed. */
  signedAt: string | null;
  /**
   * Short-lived presigned S3 GET for the signed CMS-10882 PDF, for inline
   * viewing; null until signed.
   */
  signedDocUrl: string | null;
  /**
   * Short-lived presigned S3 GET for the same signed PDF with an
   * `attachment; filename="cms-10882.pdf"` content disposition, for direct
   * download. Non-null exactly when `signedDocUrl` is, since both derive from
   * the stored `signed_doc_key`.
   */
  signedDocDownloadUrl: string | null;
  /** Preferred pharmacy block, always a full object; each string may be blank. */
  pharmacy: Pharmacy;
  /** Structured prescription list; always an array (the backend defaults to `[]`). */
  prescriptionsList: Prescription[];
  pharmacyReceipts: PortalAttachment[];
  paymentBills: PortalAttachment[];
}

/**
 * Body of `POST /api/portal/enrollments/{id}/sign`: the in-portal CMS-10882
 * e-sign, authorized by the portal session bearer with no separate token.
 * Mirrors `PortalSignRequest` in server/app/models/api/portal.py. Returns the
 * re-read `PortalEnrollmentDetail`.
 */
export interface PortalSignRequest {
  /** Base64 PNG of the drawn signature. The client omits the `data:image/png;base64,` prefix; the server strips one if present. */
  signaturePngBase64: string;
  typedName: string;
  intent: boolean;
}

/**
 * A signable document's signing status. Mirrors `DocumentStatus` in
 * server/app/models/db/enrollment.py. `requested` means a signature request
 * was attempted and no message was accepted (for example, no email on file).
 */
export type DocumentStatus = "none" | "requested" | "sent" | "signed";

/**
 * One signable document on an enrollment, from
 * `GET /api/portal/enrollments/{id}/documents` (registration order) and the
 * response of `POST .../documents/{documentId}/sign`. Mirrors
 * `PortalSignableDocument` in server/app/models/api/portal.py. `{id}` is the
 * full enrollment id or its short id.
 */
export interface PortalSignableDocument {
  /** The registry id, e.g. `cms_1696`. */
  documentId: string;
  title: string;
  status: DocumentStatus;
  /** True when this session may sign the document now: unsigned, an allowed signer role, and the document's own gates pass. A signed one 409s. */
  signable: boolean;
  /**
   * The recorded signer once signed, else the signer who would sign it: this
   * session's signer when its role may sign (the confirmed caregiver for a
   * caregiver session, the participant for a member session), else an allowed
   * signer, so a caregiver session sees the participant on CMS-1696.
   */
  signerName: string;
  /** The role of `signerName`; it differs from the session's own signer role when the session can't sign the document. */
  signedAs: "patient" | "caregiver";
  /** The statement the sign page shows verbatim next to the intent checkbox; null for a document without its own consent text. */
  intentText: string | null;
  /** The consent-text version to echo back on sign; null when `intentText` is null. */
  consentVersion: string | null;
  /** ISO 8601 UTC of the first signature request; a resend keeps it. Null when never requested. */
  requestedAt: string | null;
  /** ISO 8601 UTC; null until signed. */
  signedAt: string | null;
  /** Short-lived presigned GET of the signed PDF for inline viewing; null until signed. */
  signedDocUrl: string | null;
  /** The same PDF with an attachment content disposition; non-null exactly when `signedDocUrl` is. */
  signedDocDownloadUrl: string | null;
}

/**
 * Body of `POST /api/portal/enrollments/{id}/documents/{documentId}/sign`.
 * Mirrors `PortalDocumentSignRequest` in server/app/models/api/portal.py.
 * `consentVersion` echoes `PortalSignableDocument.consentVersion` exactly; a
 * document with a consent version 422s a mismatched echo.
 */
export interface PortalDocumentSignRequest extends PortalSignRequest {
  consentVersion: string | null;
}

/**
 * Body of `POST /api/portal/enrollments/{id}/prescriptions`: one catalog drug
 * to append to the enrollment's prescription list. Mirrors
 * `PortalPrescriptionAddRequest` in server/app/models/api/portal.py. Returns
 * the re-read `PortalEnrollmentDetail`; 409 when the rxcui is already listed,
 * 422 while no plan is assigned, for an unknown rxcui or a full list.
 */
export interface PortalPrescriptionAddRequest {
  rxcui: string;
}

/**
 * The patient's caregiver as carried by `PortalDashboard`. Mirrors
 * `PortalCaregiver` in server/app/models/api/portal.py.
 *
 * The caregiver lives on the patient row and is invited by email; `status` is
 * `"pending"` until the caregiver accepts the invitation, then `"confirmed"`.
 */
export interface PortalCaregiver {
  firstName: string;
  lastName: string;
  relationship: string;
  /** Null only on a row migrated from an older enrollment-level caregiver. */
  email: string | null;
  /** E.164. */
  phone: string;
  preferredLanguage: PreferredLanguage;
  address: CaregiverAddress;
  status: "pending" | "confirmed";
  /** ISO 8601 UTC. */
  invitedAt: string;
  /** ISO 8601 UTC; always `invitedAt` plus one hour, meaningful only while `status` is `"pending"`. */
  inviteExpiresAt: string;
  /** ISO 8601 UTC; null while `status` is `"pending"`. */
  confirmedAt: string | null;
}

/**
 * The caregiver's address as written by `PortalCaregiverPut`. Mirrors
 * `CaregiverAddressWrite` in server/app/models/api/portal.py: every field but
 * `street2` is required, `state` is a USPS code and `zip` matches
 * `^\d{5}(-\d{4})?$`.
 */
export interface CaregiverAddressWrite {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Body of `PUT /api/portal/caregiver` (portal session bearer, members only):
 * adds the patient's one caregiver and emails the invitation. Mirrors
 * `PortalCaregiverPut` in server/app/models/api/portal.py. Returns the
 * `PortalCaregiver`; 403 on a caregiver session, 409 while a caregiver
 * exists, 422 when `phone` duplicates the patient's phone or home phone, 429
 * during the send cooldown. `phone` is sent as typed; the server normalises
 * it to E.164. `consent` must be true.
 */
export interface PortalCaregiverPut {
  firstName: string;
  lastName: string;
  relationship: string;
  email: string;
  phone: string;
  preferredLanguage: PreferredLanguage;
  address: CaregiverAddressWrite;
  consent: boolean;
}

/**
 * Body of the public `POST /api/portal/caregiver/accept`: the invitation
 * token from the emailed link. Mirrors `PortalCaregiverAcceptRequest` in
 * server/app/models/api/portal.py. Every failure is a 410 with one opaque
 * detail.
 */
export interface PortalCaregiverAcceptRequest {
  token: string;
}

/**
 * Response of `POST /api/portal/caregiver/accept`. Mirrors
 * `PortalCaregiverAcceptResponse` in server/app/models/api/portal.py.
 */
export interface PortalCaregiverAcceptResponse {
  patientFirstName: string;
}

/**
 * Response of `GET /api/portal/dashboard` (portal session bearer): the member
 * dashboard payload. Mirrors `PortalDashboard` in
 * server/app/models/api/portal.py.
 */
export interface PortalDashboard {
  /**
   * The session's role. It drives which controls the SPA shows (a caregiver
   * session gets a read-only caregiver tile); authorization stays on the
   * server. Mirrors `PortalDashboard.role`.
   */
  role: "member" | "caregiver";
  firstName: string;
  /** From the same patient profile row as `firstName`; "" when that row is missing or deactivated. */
  lastName: string;
  /** ISO 8601 UTC; null when the patient row has none. */
  savingsLastUpdatedAt: string | null;
  latestEnrollment: PortalDashboardEnrollment | null;
  /**
   * The M3P payment projection for the latest enrollment's drugs under its
   * assigned plan, with YTD TrOOP from the patient's persisted Blue Button
   * claims; null when the member has no enrollment.
   */
  m3p: M3pProjection | null;
  /**
   * The same enrollment priced under next contract year's datasets
   * (`pricesEstimated`) while `GET /api/plans/years` offers that year, else
   * null. An assigned plan with no next-year snapshot is a `plan_unknown`
   * block carrying that `contractYear`, not null, so the SPA can say the plan
   * is not offered.
   */
  m3pNextYear: M3pProjection | null;
  medicareConnection: PortalDashboardMedicareConnection;
  /** The patient's caregiver; null until the member or an admin adds one. */
  caregiver: PortalCaregiver | null;
  /** The pending account-closure request; null when none is pending. */
  accountClosure: PortalAccountClosure | null;
}

/**
 * A pending account-closure request. Mirrors `PortalAccountClosure` in
 * server/app/models/api/portal.py; the member dashboard and the admin patient
 * and enrollment reads share it.
 *
 * `POST /api/portal/account-closure` (portal session bearer, members only)
 * stamps the request and returns this shape; `DELETE` withdraws it with a
 * 204. Both answer 409 when the stamp is already in the requested state and
 * 403 on a caregiver session. A daily job deletes the patient's data once
 * `scheduledFor` has passed; the grace period is defined only on the server.
 */
export interface PortalAccountClosure {
  /** ISO 8601 UTC. */
  requestedAt: string;
  /** ISO 8601 UTC; when the deletion falls due. */
  scheduledFor: string;
}

/**
 * The member's Blue Button (Medicare) connection as carried by
 * `PortalDashboard`. Mirrored from `PortalDashboardMedicareConnection` in
 * `server/app/models/api/portal.py`.
 */
export interface PortalDashboardMedicareConnection {
  connected: boolean;
  /**
   * ISO 8601 UTC: the newer of the connection's creation time and the last
   * Blue Button data fetch. Null whenever `connected` is false.
   */
  lastSyncedAt: string | null;
  /**
   * ISO 8601 UTC: the connection row's creation time. Null whenever
   * `connected` is false.
   */
  connectedAt: string | null;
}

/**
 * The member's latest enrollment as carried by `PortalDashboard`: the slice the
 * dashboard needs to open the in-dashboard sign view. Mirrors
 * `PortalDashboardEnrollment` in server/app/models/api/portal.py.
 */
export interface PortalDashboardEnrollment {
  id: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  status: EnrollmentStatus;
  esignStatus: EsignStatus;
  signable: boolean;
  signerName: string;
  signedAs: "patient" | "caregiver";
  /** The M3P plan-enrollment lifecycle status; null while no plan is assigned. */
  m3pStatus: M3pStatus | null;
  /**
   * The M3P plan-enrollment effective date (YYYY-MM-DD); null while no plan is
   * assigned or before the plan enrollment is confirmed.
   */
  m3pEffectiveDate: string | null;
  /** The contract year the plan was picked for (`EnrollmentRecord.plan_year`); null reads as this year. */
  planYear: number | null;
  /** The enrollment's prescription snapshots, in list order. */
  prescriptionsList: Prescription[];
}

/**
 * Response of `GET /api/portal/bluebutton` (portal session bearer): the
 * portal's Medicare-connect status. Mirrors `PortalBlueButtonStatus` in
 * server/app/models/api/portal.py. No SPA calls this endpoint.
 *
 * `enabled` is true when the `bbMemberConnect` flag is on and Blue Button
 * credentials are configured. `connected` is true when a Blue Button connection
 * exists for the session's patient. `sandbox` is true when the server's Blue
 * Button base URL targets the CMS sandbox host.
 */
export interface PortalBlueButtonStatus {
  enabled: boolean;
  connected: boolean;
  sandbox: boolean;
}

/**
 * Response of `POST /api/portal/bluebutton/connect` (no body): the CMS-hosted
 * consent URL for a full-page navigation. Mirrors
 * `PortalBlueButtonConnectResponse` in server/app/models/api/portal.py. No SPA
 * calls this endpoint. Errors: `409` when the feature is off or Blue Button is
 * unconfigured, `401` for a stale session. After consent the server redirects
 * the browser to the portal root with `?bb=connected` or
 * `?bb=error&reason=…`.
 */
export interface PortalBlueButtonConnectResponse {
  authorizeUrl: string;
}

/**
 * What happened, as the member timeline labels it. Mirrors `ActivityKind` in
 * server/app/models/db/activity.py.
 */
export type PortalActivityKind =
  | "call_booked"
  | "call_reminder_sent"
  | "call_started"
  | "call_completed"
  | "sign_link_sent"
  | "signed"
  | "upload_link_sent"
  | "document_uploaded"
  | "consent_link_sent"
  | "mail_received"
  | "alias_created"
  | "consent_pack_signed"
  | "enrollment_created"
  | "enrollment_status_changed"
  | "agent_action"
  | "sequence_email_sent";

/**
 * One timeline entry of `GET /api/portal/activity` (portal session bearer).
 * Mirrors `PortalActivityItem` in server/app/models/api/portal.py. `summary`
 * is the member-facing text; `channel` is the delivery surface (`chat`,
 * `voice`, `sms`, `portal` or `email`) or null when the event has none.
 */
export interface PortalActivityItem {
  /** ISO 8601 UTC. */
  at: string;
  kind: PortalActivityKind;
  summary: string;
  channel: string | null;
}

/**
 * One newest-first page of `GET /api/portal/activity`. Mirrors
 * `PortalActivityPage` in server/app/models/api/portal.py. `nextCursor` is an
 * opaque forward-only token, null exactly when the log is exhausted; pass it
 * back as `?cursor=` for the next page.
 */
export interface PortalActivityPage {
  items: PortalActivityItem[];
  nextCursor: string | null;
}

/**
 * One received plan-correspondence email of `GET /api/portal/mail` (portal
 * session bearer; 503 while mail is unconfigured). Mirrors `PortalMailItem`
 * in server/app/models/api/portal.py. `messageId` keys the per-message
 * download route; `label` is the sender-domain classification
 * (`plan_correspondence` or anything else, rendered as "Other").
 */
export interface PortalMailItem {
  messageId: string;
  /** ISO 8601 UTC. */
  receivedAt: string;
  fromDomain: string;
  subject: string;
  label: string;
}

/**
 * One newest-first page of `GET /api/portal/mail`; same cursor contract as
 * `PortalActivityPage`. Mirrors `PortalMailPage` in
 * server/app/models/api/portal.py.
 */
export interface PortalMailPage {
  items: PortalMailItem[];
  nextCursor: string | null;
}

/**
 * Response of `GET /api/portal/mail/{messageId}/download`: a short-lived
 * presigned GET for the raw `.eml`. Mirrors `PortalMailDownload` in
 * server/app/models/api/portal.py.
 */
export interface PortalMailDownload {
  url: string;
}

/**
 * Body of `POST /api/portal/chat` (portal session bearer): one member chat
 * turn. Mirrors `PortalChatRequest` in server/app/models/api/portal.py. Omit
 * `sessionId` (or pass null) to start a new chat session; echo back the
 * previous response's `sessionId` to continue one.
 */
export interface PortalChatRequest {
  message: string;
  sessionId?: string | null;
}

/**
 * Response of `POST /api/portal/chat`. Mirrors `PortalChatResponse` in
 * server/app/models/api/portal.py. `escalated` means a specialist follows up;
 * `done` means the agent finished and the session should close. `502` means
 * the chat service is unreachable.
 */
export interface PortalChatResponse {
  sessionId: string;
  reply: string;
  escalated: boolean;
  done: boolean;
}

/**
 * Mid-call action-link wire shapes, mirrored from
 * server/app/models/api/action_links.py. The `/do` page in the member SPA
 * reads the token from the URL fragment and replays it only as
 * `Authorization: Bearer`; every token failure is the same opaque 401.
 */

/** The link's purpose. Mirrors `ActionLinkAction` in server/app/services/action_link_token.py. */
export type ActionLinkAction = "sign" | "upload" | "consent";

/**
 * The sign page's one-line what-you-are-signing summary. Mirrors
 * `ActionLinkEnrollmentSummary` in server/app/models/api/action_links.py.
 * `planName` is null when no plan is assigned or its catalogue row is gone.
 */
export interface ActionLinkEnrollmentSummary {
  planName: string | null;
  status: string;
}

/** Why a sign link can't be signed, in gate order: a signature wins, then approval, then completeness. */
export type ActionLinkSignBlockedReason = "signed" | "not_approved" | "incomplete";

/**
 * Response of `GET /api/links/context` (action-link token bearer). Mirrors
 * `ActionLinkContext` in server/app/models/api/action_links.py.
 * `signAllowed` is set for sign and consent links; `enrollmentSummary` for a
 * sign link only; `field` for an upload link only (the document class it
 * accepts); `consentStatus` (`none`, `signed` or `expired`), `intentText` and
 * `consentVersion` for a consent link only. A consent link's `signAllowed` is
 * false while a current CMS-1696 signature exists or when the link holder is
 * not an allowed signer (a caregiver). All of these may be absent.
 */
export interface ActionLinkContext {
  action: ActionLinkAction;
  patientFirstName: string;
  signAllowed?: boolean | null;
  /** First failing sign gate; null when signAllowed is true and for non-sign links. */
  signBlockedReason?: ActionLinkSignBlockedReason | null;
  enrollmentSummary?: ActionLinkEnrollmentSummary | null;
  field?: string | null;
  consentStatus?: string | null;
  /** The statement the signer affirms, shown verbatim as the intent checkbox label. */
  intentText?: string | null;
  /** The consent text's version, echoed back as `ActionLinkSignRequest.consentVersion`. */
  consentVersion?: string | null;
}

/**
 * Body of `POST /api/links/sign` and `POST /api/links/consent/sign` (action
 * -link token bearer). Mirrors `ActionLinkSignRequest` in
 * server/app/models/api/action_links.py; same shape as `PortalSignRequest`.
 * Both return `{"signedAt": ...}`.
 */
export interface ActionLinkSignRequest {
  /** Base64 PNG of the drawn signature; the `data:image/png;base64,` prefix may be omitted. */
  signaturePngBase64: string;
  typedName: string;
  intent: boolean;
  /**
   * The context's `consentVersion`, the version of the intent text shown. The
   * consent sign rejects a mismatch with 422; the CMS-10882 sign ignores it.
   */
  consentVersion?: string | null;
}

/**
 * Response of `POST /api/links/upload` (multipart `file`; action-link token
 * bearer): the stored, sanitised filename. Mirrors `ActionLinkUploadResult`
 * in server/app/models/api/action_links.py.
 */
export interface ActionLinkUploadResult {
  filename: string;
  /** ISO 8601 UTC. */
  uploadedAt: string;
}

/**
 * Public Blue Button wire shapes for the M3P enrollment flow's Medicare
 * connect, mirrored from server/app/models/api/bluebutton.py. All three
 * endpoints are public because the customer SPA is unauthenticated. They stay
 * separate from the portal's `PortalBlueButton*` shapes so each wire can
 * change on its own.
 */

/**
 * Response of `GET /api/bluebutton/form-status`: whether the flow offers the
 * Medicare connect. True when the `bbMemberConnect` flag is on and Blue Button
 * credentials are configured; the prod CDK config leaves Blue Button
 * unconfigured.
 */
export interface BlueButtonFormStatus {
  enabled: boolean;
}

/**
 * Response of `POST /api/bluebutton/form-connect?flow=enroll` (no body): the
 * CMS-hosted consent URL for a full-page navigation (`window.location.assign`,
 * not an in-app route). Errors: `409` when the feature is off or Blue Button is
 * unconfigured, `422` without `flow=enroll`, `429` past the per-IP rate limit.
 * After consent the server redirects to `/enroll/verifying?bb=<handle>` on
 * success or `/enroll/start?bb=error&reason=<slug>` on failure.
 */
export interface BlueButtonFormConnectResponse {
  authorizeUrl: string;
}

/**
 * Response of `GET /api/bluebutton/form-prefill?token=<handle>`: the parsed
 * FHIR Patient demographics behind a `?bb=<handle>` callback. Mirrors
 * `BlueButtonFormPrefill` in server/app/models/api/bluebutton.py.
 *
 * Non-consuming and short-lived (the server's `_PREFILL_TTL_SECONDS`); an
 * unknown, expired or claimed handle is a `404`. Name, dob and gender are null
 * when absent; address fields are blank strings when absent, since real Blue
 * Button often returns only state and ZIP. `gender` carries only the two values
 * the form maps 1:1; `other` and `unknown` are never guess-mapped. A member who
 * withholds personal information at the CMS consent screen gets null name, dob
 * and gender and a blank address. `mbi` and `mbiMasked` are `""` when neither
 * the Patient resource nor a Coverage resource carried a Medicare number. The
 * plan and coverage block feeds the flow's verify and plan screens, each field
 * null when Blue Button's Coverage or EOB data didn't carry it.
 */
export interface BlueButtonFormPrefill {
  firstName: string | null;
  lastName: string | null;
  /** `YYYY-MM-DD` (the form's composed dob format) or null. */
  dob: string | null;
  gender: "male" | "female" | null;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  /** The raw Medicare number, or `""` when CMS supplied none. */
  mbi: string;
  planName: string | null;
  /**
   * Plans-catalogue plan id the server matched from the member's Part D
   * contract and PBP at the callback. When set, `planName` carries that plan's
   * marketing name. Null when no catalogue plan matched.
   */
  planId: string | null;
  /** e.g. `"Medicare Advantage (Part C + D)"`. */
  planType: string | null;
  coverageStatus: string | null;
  /** ISO date (`YYYY-MM-DD`) or null. */
  coverageStart: string | null;
  /** Masked MBI: the first 4 characters kept and the rest `X`-ed, as `<first4>-XXXX-XXXX`; `""` when `mbi` is. */
  mbiMasked: string;
  /**
   * The member's current-year Part D TrOOP (Σ patientPaid + otherTroop +
   * licsPaid over this year's PDE claims), summed at Blue Button connect time;
   * null when unknown (the claims fetch failed). The picker's and the review
   * step's projection requests carry it as `ytdTroop`. The picker's
   * year-to-date line shows it read-only and is hidden while it is null.
   */
  ytdTroop: number | null;
  /**
   * The member's Part D fills since January 1 of last year on prod (the full
   * history elsewhere), one entry per drug, in the display order the server
   * sets (see `rx_history` on the server model). Null when unknown (the claims
   * paging failed, or the prefill row lacks it); `[]` when the member has no
   * fills. The picker renders it as given and never re-sorts it.
   */
  rxHistory: RxHistoryEntry[] | null;
  /** The prefill plan's Part D deductible in dollars; null when the plan or its formulary snapshot is unknown. */
  deductible: number | null;
}

/**
 * One row of the member's Blue Button prescription history. Mirrors
 * `RxHistoryEntry` in server/app/models/api/bluebutton.py.
 *
 * A fill whose NDC resolves to a drugs-catalog rxcui carries that `drug`, and
 * fills of the same rxcui merge, keeping the latest pickup. An unmatched fill
 * is read-only: `drug` and `coverage` are null, and `name` is the claim's own
 * drug name. Medication history is PHI: it lives only on the short-TTL
 * prefill and in the flow's sessionStorage, never in `POST /enrollments`.
 */
export interface RxHistoryEntry {
  /** Latest pickup among the merged fills, YYYY-MM-DD. */
  lastFilled: string;
  /** Catalog drug; null = unmatched, rendered read-only. */
  drug: Drug | null;
  /** Coverage under the prefill's plan; null when `drug` is null or the plan is unknown. */
  coverage: DrugCoverageEntry | null;
  /** Display name for a read-only row (Blue Button NDC display, else RxNav conceptName). */
  name: string;
}

/**
 * LIS ("Extra Help") eligibility-questionnaire wire shapes, mirrored from
 * server/app/models/api/lis.py. `PortalLisState` mirrors the portal
 * endpoints' state, which no SPA reads. The admin patient LIS tab reuses
 * `LisInterviewQuestion`, `LisAnsweredQuestion` and `LisVerdict` through the
 * `AdminLisState` mirrors in `frontend/admin/src/types/admin.ts`. Nullable
 * fields arrive as JSON `null`, never omitted.
 */

export type LisQuestionType = "choice" | "multi_choice" | "number" | "bool";

export type PortalLisStatus = "not_started" | "in_progress" | "completed" | "unavailable";

/**
 * An answer value by question type. `choice` takes the option value (a number
 * for `household`). `multi_choice` takes a list of program values, where `[]`
 * means "None of these": clients map the `none` sentinel to `[]` before
 * submit, and the server rejects `"none"` inside the list. `number` takes a
 * finite float ≥ 0, and `bool` a boolean.
 */
export type LisAnswerValue = number | string | boolean | string[];

/** One offered option: `value` is a number for `household` (1 or 2) and a program string for `enrolled`. */
export interface LisQuestionOption {
  value: number | string;
  label: string;
}

/**
 * One question exactly as the engine poses it (`text` and `why` verbatim).
 * Mirrors `LisQuestion` in server/app/models/api/lis.py.
 */
export interface LisInterviewQuestion {
  id: string;
  type: LisQuestionType;
  text: string;
  /** Helper copy explaining why the question is asked. */
  why: string;
  /** `"USD/month"` | `"USD"` for `number` questions; null otherwise. */
  unit: string | null;
  /** Offered options for the choice types; null for `number`/`bool`. */
  options: LisQuestionOption[] | null;
}

/**
 * An as-asked snapshot from the stored block. Render this text, never
 * re-derived text. `answerLabel` is set for the choice types only
 * (comma-joined labels, or "None of these" for `multi_choice`).
 */
export interface LisAnsweredQuestion extends LisInterviewQuestion {
  answer: LisAnswerValue;
  answerLabel: string | null;
  answeredAt: string;
}

/** One income tier the answers passed (`allTiers` entry). */
export interface LisTierPass {
  tier: string;
  limit: number;
  marginPct: number;
  /** State-specific program name (e.g. "QI-1"); null when the federal tier name applies. */
  stateName: string | null;
}

/** One tier missed within the near-miss band (`near` entry). */
export interface LisNearMiss {
  tier: string;
  limit: number;
}

/**
 * The application kit: a verbatim snake_case passthrough of the vendored state
 * file's `apply` object, which the server echoes untransformed. These types
 * keep the snake_case keys on purpose; don't camelCase them.
 */
export interface LisApplicationForm {
  title: string;
  revision: string | null;
  /**
   * Not always a URL; some state files carry free text here. Link it only when
   * the value starts with `"http"`, and otherwise render it as plain text.
   */
  url: string | null;
  no_dedicated_form: boolean;
}

/** One submission channel (`kind`: mail | fax | portal | in_person | phone). */
export interface LisApplicationChannel {
  kind: string;
  purpose: string;
  payload: { text: string };
}

/** One assist contact (typically `kind: "phone"` with dialable `numbers`). */
export interface LisApplicationAssist {
  kind: string;
  payload: { text: string; numbers?: string[] };
}

export interface LisApplicationKit {
  form: LisApplicationForm;
  channels: LisApplicationChannel[];
  assist: LisApplicationAssist[];
}

export type LisVerdictResult = "deemed" | "likely_eligible" | "apply_anyway" | "check_federal_door";

export type LisVerdictDegree =
  | "automatic"
  | "strong"
  | "clear"
  | "close"
  | "near_miss"
  | "none_at_state";

/**
 * State pharmaceutical assistance program (SPAP) guidance attached to a
 * verdict. Mirrors `LisSpapGuidance`, the model of `LisVerdict.spap`, in
 * server/app/models/api/lis.py.
 *
 * The program text is a snapshot taken when the questionnaire completed, so a
 * stored verdict keeps the text it was given. `message` is hedged guidance
 * copy; render it verbatim. Only the admin patient LIS tab renders this.
 */
export interface LisSpapGuidance {
  /** Program name, e.g. "PACENET". */
  program: string;
  /** `likely`: income at or under the limit. `near_miss`: over it, within the near-miss band. */
  fit: "likely" | "near_miss";
  /** Monthly dollars for the answered household; null when the program has no income limit. */
  limit: number | null;
  message: string;
  benefit: string;
  requirements: string;
  apply: string;
  /** The limit as the state publishes it, e.g. "up to $14,500 single / $17,700 couple a year". */
  publishedLimit: string;
  /** The state's source pages, as https URLs. */
  sourceUrls: string[];
  /** The SPAP data file's version, e.g. "2026-07-31". */
  version: string;
}

/**
 * The engine's verdict. Mirrors `LisVerdict` in server/app/models/api/lis.py.
 *
 * Which optional fields are set depends on `result`: `likely_eligible` carries
 * the passed tiers and `apply_anyway` the near misses, and both carry
 * `assetStatus`, `checklist` and `application`. `deemed` and
 * `check_federal_door` carry none of them. `message` is member-facing copy;
 * render it verbatim.
 */
export interface LisVerdict {
  result: LisVerdictResult;
  degree: LisVerdictDegree;
  message: string;
  /** `"apply"` | `"lis_direct_or_ship"`, or a full sentence for `deemed`. */
  nextStep: string;
  state: string;
  /** The state data file's human-readable verification stamp. */
  verified: string;
  tier: string | null;
  marginPct: number | null;
  allTiers: LisTierPass[] | null;
  near: LisNearMiss[] | null;
  assetStatus: "no_test" | "pass" | "over_but_apply" | "unknown" | null;
  checklist: string[] | null;
  application: LisApplicationKit | null;
  /** Null when no state program applies, and for a stored verdict that has no SPAP data. */
  spap: LisSpapGuidance | null;
}

/**
 * The full questionnaire state from `GET /api/portal/lis` and every mutation's
 * echo. Mirrors `PortalLisState` in server/app/models/api/lis.py.
 *
 * `enabled` is the `lisQuestionnaire` flag; the GET reports the real status
 * either way. `state` is null exactly when `status === "unavailable"`, and
 * `nextQuestion` is null when completed or unavailable. Errors: 401 is a dead
 * session; a mutation 409 means the flag is off or the block is completed or
 * unavailable (refetch the GET); 422 is an out-of-order or malformed answer.
 */
export interface PortalLisState {
  enabled: boolean;
  role: "member" | "caregiver";
  status: PortalLisStatus;
  state: string | null;
  questionsVersion: string | null;
  /** Role that submitted the first answer, which stays the starter's role;
   * `"admin"` when an admin started the block from the patient LIS tab. */
  answeredBy: "member" | "caregiver" | "admin" | null;
  startedAt: string | null;
  completedAt: string | null;
  answeredQuestions: LisAnsweredQuestion[];
  nextQuestion: LisInterviewQuestion | null;
  verdict: LisVerdict | null;
}

/**
 * Body of the LIS answer POSTs (`POST /api/portal/lis/answers` and
 * `POST /api/admin/patients/{id}/lis/answers`). `questionId` must equal the id
 * of the server-derived next question, which guards against out-of-order
 * submits: a stale submit gets a 422, not corruption.
 */
export interface LisAnswerRequest {
  questionId: string;
  answer: LisAnswerValue;
}

/**
 * Partner-portal auth wire shapes, mirrored from
 * server/app/models/api/partner.py. Auth is a self-issued partner-session JWT,
 * not OIDC, carried as `Authorization: Bearer <session>`. A reset link's
 * `POST /api/partner/auth/set-password` mints it directly; otherwise
 * `POST /api/partner/auth/signup/verify` or `POST /api/partner/auth/login/verify`
 * mints it once the OTP is accepted.
 */
export interface PartnerSetPasswordRequest {
  /** Single-use code from the emailed link. */
  code: string;
  password: string;
  /**
   * Display name the user picks while setting their password. The handler
   * requires it (422) only when the account has no name yet, as on a fresh
   * onboard or invite link (see `PartnerLinkInfoResponse.needsName`), and
   * ignores it for an account that already has one.
   */
  name?: string;
  /**
   * Phone number entered at first-time setup (an onboard or invite link).
   * Required server-side for a first-time link (see
   * `PartnerLinkInfoResponse.firstTimeSetup`): the server normalizes it to
   * E.164 (422 if unparseable) and OTP-verifies it before creating the account.
   * A reset link ignores it, and the user keeps their phone.
   */
  phone?: string;
}

/**
 * Response of `GET /api/partner/auth/link-info?code=<code>`: what the
 * set-password form should collect. `needsName` is true for an onboard or
 * invite link whose account has no name yet. It is false for a reset and also
 * for an invalid, expired or used code, so the endpoint is no link-state
 * oracle; the set-password 400 surfaces a bad link on submit.
 *
 * `firstTimeSetup` is true for an onboard or invite link, whose form must
 * collect a phone and run the OTP; false for a reset or an invalid code. Gate
 * the phone field on it, not on `needsName`: a named org-member invite has
 * `needsName=false` but is still a first-time setup that needs a phone. In the
 * GPO realm it is also false for a first-time link whose account already has a
 * verified phone; see `GpoLinkInfoResponse`.
 */
export interface PartnerLinkInfoResponse {
  needsName: boolean;
  firstTimeSetup: boolean;
  /**
   * The invite's stored name and phone for a first-time link: defaults the
   * setup form starts from, which the invitee may change. Both are "" for a
   * reset link and for a bad code.
   */
  prefillName: string;
  prefillPhone: string;
}

/**
 * Response of `POST /api/partner/auth/set-password`: exactly one of the two is
 * non-null. A reset link returns a minted `session` directly, with no OTP. A
 * first-time setup (onboard or invite) link returns a short-lived `challenge`
 * instead: the account is not created and the link not burned until
 * `POST /api/partner/auth/signup/verify` accepts the OTP.
 */
export interface PartnerSetPasswordResponse {
  session: string | null;
  challenge: string | null;
  /**
   * `true` only when the SMS-OTP bypass is in effect, which the server never
   * allows in prod. Meaningful only with `challenge`, where it shows the
   * `BypassOtpBanner` on `/verify-phone`; always `false` on the reset branch.
   */
  bypassSmsOtp: boolean;
}

/**
 * Body of `POST /api/partner/auth/signup/verify`, which accepts the
 * first-time-setup OTP. `challenge` is the opaque token set-password returned,
 * carrying the resolved link code, the entered phone and the name. `code` is
 * the OTP the user typed, not the link code. `password` is re-submitted here
 * rather than carried in the challenge.
 */
export interface PartnerSignupVerifyRequest {
  challenge: string;
  code: string;
  password: string;
}

/**
 * Response of `POST /api/partner/auth/signup/verify`: exactly one is non-null.
 * On an accepted OTP `session` is a minted partner-session JWT (account
 * created, link burned). Otherwise `reason` carries a Telnyx OTP code
 * (`"rejected"`, `"expired"`, `"max_attempts_exceeded"`) or `"phone_in_use"`:
 * after the OTP was accepted, the phone-uniqueness check found the number
 * registered to another partner. The setup link isn't burned then, so the user
 * can retry with a different number. No account is created in any non-session
 * case.
 */
export interface PartnerSignupVerifyResponse {
  session: string | null;
  reason: string | null;
}

/**
 * Body of `POST /api/partner/auth/signup/resend`, the only send path for the
 * first-time-setup OTP: it makes the first send and every resend, because
 * set-password mints the challenge without sending a code. `challenge` is the
 * token from set-password, and the phone comes from inside it, never from the
 * client. `channel` picks text or voice-call delivery (default `"sms"`).
 * Responds `{ ok: true }`, or 429 within `SEND_COOLDOWN_SECONDS` of the
 * previous send (one cooldown shared across both channels).
 */
export interface PartnerSignupResendRequest {
  challenge: string;
  channel?: OtpChannel;
}

/**
 * Body of `PATCH /api/partner/me`: edit your own display name. `name` is
 * required and non-blank (the server trims it; a blank one is a 422). Returns
 * the full updated `PartnerMeResponse`.
 */
export interface PartnerProfileUpdate {
  name: string;
}

/**
 * Body of `POST /api/partner/orgs`: an account owner creates an organization.
 * Mirrors `PartnerOrgCreateRequest` in server/app/models/api/partner.py.
 *
 * A non-blank `name` plus the org's `organizationType`, fixed at creation. The
 * creator becomes the org's first `org_admin`, active immediately. The response
 * is a `PartnerMembershipOut`, the same shape as the `GET /api/partner/me`
 * memberships, so there is no separate create-response type.
 */
export interface PartnerOrgCreateRequest {
  name: string;
  /** Fixed at creation; the profile PUT doesn't carry it. */
  organizationType: OrgType;
}

export interface PartnerLoginRequest {
  email: string;
  password: string;
}

/**
 * Partner login is two-step, with an OTP as the second factor. A correct email
 * and password at `POST /api/partner/auth/login` return a short-lived
 * `challenge` and send no code. The client requests the code through
 * `/login/resend` on a chosen channel, and `/login/verify` mints the session
 * once it accepts the OTP.
 */

/** Response of `POST /api/partner/auth/login`: the 2FA challenge. No session is
 *  minted until `/login/verify` accepts the OTP. */
export interface PartnerLoginChallengeResponse {
  challenge: string;
  /**
   * `true` only when the SMS-OTP bypass is in effect, which the server never
   * allows in prod: no code is sent, and verify accepts `BYPASS_OTP_CODE`.
   * Shows the `BypassOtpBanner` on `/verify-login`.
   */
  bypassSmsOtp: boolean;
}

/** Body of `POST /api/partner/auth/login/verify`. No password: the first factor
 *  passed at login, and the session is minted from the challenge claims. */
export interface PartnerLoginVerifyRequest {
  challenge: string;
  code: string;
}

/** Response of `POST /api/partner/auth/login/verify`: exactly one is non-null.
 *  `reason` is `"rejected"`, `"expired"` or `"max_attempts_exceeded"`. */
export interface PartnerLoginVerifyResponse {
  session: string | null;
  reason: string | null;
}

/** Body of `POST /api/partner/auth/login/resend`, the only send path for the
 *  login OTP: it makes the first send and every resend, because `/login` mints
 *  the challenge without sending a code. The phone comes from inside the
 *  challenge, never from the client; `channel` picks text or voice-call
 *  delivery (default `"sms"`). */
export interface PartnerLoginResendRequest {
  challenge: string;
  channel?: OtpChannel;
}

export interface PartnerForgotPasswordRequest {
  email: string;
}

export interface PartnerSessionResponse {
  /** Partner-session JWT; carry as `Authorization: Bearer <session>`. */
  session: string;
}

/**
 * GPO-portal wire shapes, mirrored from server/app/models/api/gpo.py. A GPO
 * (group purchasing organization) is its own tenant: separate accounts,
 * memberships and an HS256 session (`typ="gpo"`) that `/api/gpo/*` accepts and
 * `/api/partner/*` rejects, and vice versa. The SPA serves one GPO per host and
 * derives the tenant `slug` from the hostname.
 *
 * The `/api/gpo/auth/*` endpoints mirror the partner ones one to one, so the
 * realm-neutral shapes alias the `Partner*` types they share; `login` and
 * `forgot-password` additionally carry the `slug`, which binds the session to
 * that GPO. Unknown slug, disabled GPO and no active membership in that GPO all
 * answer the same generic 401 as a wrong password.
 */

/**
 * Response of the public `GET /api/gpos/{slug}`: the branding the login page
 * and the shell show. 404 for an unknown or disabled slug (same body for both).
 */
export interface GpoTenantResponse {
  slug: string;
  name: string;
  /** `https://` logo URL, or null when the GPO has none. */
  logoUrl: string | null;
}

export interface GpoLoginRequest {
  email: string;
  password: string;
  /** The tenant slug of the host the SPA is served from. */
  slug: string;
}

export type GpoLoginChallengeResponse = PartnerLoginChallengeResponse;
export type GpoLoginVerifyRequest = PartnerLoginVerifyRequest;
export type GpoLoginVerifyResponse = PartnerLoginVerifyResponse;
export type GpoLoginResendRequest = PartnerLoginResendRequest;

export interface GpoForgotPasswordRequest {
  email: string;
  /** The tenant slug; the reset link targets that GPO's host. */
  slug: string;
}

/**
 * As `PartnerLinkInfoResponse`, except that the phone is verified once per
 * account, on the first link completed in any GPO: a first-time (`onboard` or
 * `invite`) link for an account whose phone is already verified, as when
 * someone joins a second GPO, comes back `firstTimeSetup: false` (with
 * `needsName` as for any link), so the form collects no phone and
 * set-password mints the session directly. An account with no phone yet gets
 * `firstTimeSetup: true` and the OTP flow.
 */
export type GpoLinkInfoResponse = PartnerLinkInfoResponse;
export type GpoSetPasswordRequest = PartnerSetPasswordRequest;
/**
 * As `PartnerSetPasswordResponse`. A first-time (`onboard` or `invite`) link
 * activates the membership of the link's GPO: through a `challenge`, once
 * `/signup/verify` accepts the OTP, when the account has no verified phone
 * yet, or at once with a `session` and no `challenge` when it has one (see
 * `GpoLinkInfoResponse`). The minted session is bound to that GPO; each
 * membership has its own password, set through this link.
 */
export type GpoSetPasswordResponse = PartnerSetPasswordResponse;
export type GpoSignupVerifyRequest = PartnerSignupVerifyRequest;
export type GpoSignupVerifyResponse = PartnerSignupVerifyResponse;
export type GpoSignupResendRequest = PartnerSignupResendRequest;

/**
 * Role on a GPO membership. Mirrors the `GpoRole` literal in
 * server/app/models/db/gpo.py. Both roles add pharmacies; only `admin` manages
 * members. The owner is an `admin` who cannot be demoted or removed.
 */
export type GpoRole = "admin" | "user";

/** The signed-in GPO account, as `GET /api/gpo/me` returns it. */
export interface GpoMeUser {
  userId: string;
  email: string;
  name: string | null;
  /** E.164 phone captured and OTP-verified at first-time setup; `""` when unset. */
  phone: string;
}

/** The caller's membership in the session's GPO. */
export interface GpoMeMembership {
  role: GpoRole;
  /** True when the caller is the GPO's `owner_user_id`. */
  isOwner: boolean;
}

export interface GpoMeOwner {
  name: string | null;
  email: string;
}

/** The session's GPO, for the shell header and the Organization page. */
export interface GpoMeGpo {
  gpoId: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  /** ISO 8601 UTC timestamp of the GPO's creation. */
  createdAt: string;
  owner: GpoMeOwner;
}

/**
 * Response of `GET /api/gpo/me`: the account, its membership in the session's
 * GPO and the GPO itself. Feeds the shell header, the Profile page and the
 * Organization page. Nothing in it is editable from the GPO portal.
 */
export interface GpoMeResponse {
  user: GpoMeUser;
  membership: GpoMeMembership;
  gpo: GpoMeGpo;
}

/**
 * One row of `GET /api/gpo/members`, and the body the 201 `POST /api/gpo/members`
 * and the `PATCH /api/gpo/members/{userId}` answer. The list holds the owner
 * first, then the rest by `createdAt`; removed members are never listed.
 * Mirrors `GpoMemberOut` in server/app/models/api/gpo.py.
 */
export interface GpoMemberOut {
  userId: string;
  email: string;
  /** The account's display name; `null` until the member sets it up through an invite link. */
  name: string | null;
  /** E.164 phone OTP-verified at first-time setup; `""` when unset. */
  phone: string;
  role: GpoRole;
  /**
   * True once this GPO's invite link is completed; `false` reads as
   * "Invited". Pending or active is per membership, as is the password: an
   * email can be active in one GPO and pending in another.
   */
  active: boolean;
  /** True for the GPO's `owner_user_id`, whose role is fixed and who cannot be removed. */
  isOwner: boolean;
  /** ISO 8601 UTC timestamp of the membership's creation. */
  createdAt: string;
}

/**
 * Body of `POST /api/gpo/members` (admin only; a `user` gets 403). 409 with
 * a string detail when the email is already this GPO's owner or an active
 * member of it. A pending member here is re-invited and a removed one
 * resurrected as pending; every invite sends a link. An email may belong to
 * any number of GPOs, each membership with its own password, so a membership
 * elsewhere never blocks the invite. Mirrors `GpoMemberCreate` in
 * server/app/models/api/gpo.py.
 */
export interface GpoMemberCreate {
  email: string;
  role: GpoRole;
}

/**
 * Body of `PATCH /api/gpo/members/{userId}` (admin only). 403 on the owner,
 * 400 on the caller's own membership, 404 on a removed or unknown member.
 * Mirrors `GpoMemberRoleUpdate` in server/app/models/api/gpo.py.
 */
export interface GpoMemberRoleUpdate {
  role: GpoRole;
}

/**
 * One row of `GET /api/gpo/leaderboard`, which lists every active member of
 * the session's GPO, the owner included. The server ranks the rows: most
 * pharmacies first, a tie won by the member whose latest sign-up is older, and
 * members with 0 by name. The array order is the ranking, so a row's rank is
 * its index plus 1. Mirrors `GpoLeaderboardRow` in server/app/models/api/gpo.py.
 */
export interface GpoLeaderboardRow {
  userId: string;
  /** The account's name, or the membership's email when the account has none. */
  name: string;
  /** Every pharmacy credited to the member, in any status and through either channel. */
  signedUpCount: number;
  /**
   * The sum of `GpoPharmacyRow.m3pEnrolledCount` over the pharmacies counted in
   * `signedUpCount`. Display only; the ranking does not use it.
   */
  m3pEnrolledCount: number;
}

/**
 * Body of the super-admin `POST /api/admin/gpos`, which creates a GPO and
 * invites its owner. `slug` is lowercase letters, digits and inner hyphens, at
 * most 32 characters, outside the reserved hostnames and never `dev-`-prefixed
 * (422 otherwise; 409 when taken); it never changes. A slug a disabled GPO
 * holds is taken over only with `reuseSubdomain`. `logoUrl` is `https://`
 * only, at most 2048 characters. Mirrors `GpoCreateRequest` in
 * server/app/models/api/gpo_admin.py.
 */
export interface AdminGpoCreateRequest {
  name: string;
  slug: string;
  ownerEmail: string;
  logoUrl?: string | null;
  reuseSubdomain?: boolean;
}

/**
 * How the owner was handled at creation: always `"invited"`, since every
 * create emails the owner an onboard link to
 * `https://<slug>.<suffix>/set-password`, through which the owner sets this
 * GPO's own password, whether or not the email already belongs to other GPOs.
 */
export type AdminGpoOwnerInvite = "invited";

/**
 * Response of `POST /api/admin/gpos`. Mirrors `GpoCreateResponse` in
 * server/app/models/api/gpo_admin.py; the endpoint answers 201.
 */
export interface AdminGpoCreateResponse {
  gpoId: string;
  slug: string;
  ownerInvite: AdminGpoOwnerInvite;
}

/**
 * Onboarding state of a pharmacy a GPO signed up, derived server-side from the
 * partner org and its owner account. Mirrors the `GpoPharmacyStatus` literal in
 * server/app/services/gpo_pharmacy_status.py: `invited` (the owner has no password yet),
 * `onboarding` (profile or Terms & Conditions acceptance incomplete), `awaiting_verification`
 * (complete, not yet verified by DearBill), `active` (takes referrals) and
 * `disabled` (the org is disabled).
 */
export type GpoPharmacyStatus =
  | "invited"
  | "onboarding"
  | "awaiting_verification"
  | "active"
  | "disabled";

/** The GPO member who created the pharmacy, or whose public sign-up link it came through. */
export interface GpoPharmacySignedUpBy {
  userId: string;
  name: string | null;
}

/**
 * How a pharmacy was signed up: `"portal"` through the signed-in New pharmacy
 * dialog, `"link"` through a member's public sign-up link. Mirrors the
 * `PharmacySignupVia` literal in server/app/models/db/gpo.py. Sign-ups stored
 * without it read `null`.
 */
export type GpoPharmacySignupVia = "portal" | "link";

/**
 * The pharmacy's contact: its org owner's partner account, the contact
 * verified at sign-up. A pharmacy whose owner row is gone reads `name: null`
 * and empty `email` and `phone`. Business contact details, not patient data;
 * keep them out of logs and telemetry. Mirrors `GpoPharmacyContact` in
 * server/app/models/api/gpo.py.
 */
export interface GpoPharmacyContact {
  name: string | null;
  /** The owner's sign-in email. */
  email: string;
  /** E.164 phone; `""` when the owner has none. */
  phone: string;
}

/**
 * One row of a `GpoPharmacyPage` and the body of the 201
 * `POST /api/gpo/pharmacies` answers. Mirrors `GpoPharmacyRow` in
 * server/app/models/api/gpo.py. No patient data.
 */
export interface GpoPharmacyRow {
  orgId: string;
  name: string;
  /** `""` for a pharmacy that has not entered its NPI yet. */
  npi: string;
  /** ISO 8601 UTC timestamp of the sign-up. */
  signedUpAt: string;
  signedUpBy: GpoPharmacySignedUpBy;
  via: GpoPharmacySignupVia | null;
  contact: GpoPharmacyContact;
  status: GpoPharmacyStatus;
  /**
   * Active enrollments referred by this org whose current status is
   * `enrolled_m3p`. The 201 create response reads 0.
   */
  m3pEnrolledCount: number;
}

/**
 * Response of `GET /api/gpo/pharmacies`: one page of the pharmacies this GPO
 * signed up, newest first (ties by `orgId`, descending), at most the server's
 * `_PAGE_SIZE` rows. Mirrors `Page[GpoPharmacyRow]` in
 * server/app/models/api/pagination.py. Every query param is optional:
 *   - `rep`: a member's `userId`, matched against `signedUpBy.userId`; one the
 *     server does not know answers an empty page;
 *   - `status`: `active` (onboarding, awaiting verification or active) or
 *     `inactive` (invited or disabled);
 *   - `source`: `direct` (the New pharmacy dialog, and sign-ups stored without
 *     `via`) or `link`;
 *   - `cursor`: the previous page's `nextCursor`, sent with the same filters.
 * `total` counts the rows the filters match. `nextCursor` is an opaque keyset
 * token past the last row considered, null exactly when the filtered list is
 * exhausted. A sign-up whose org row is gone is skipped, so a page can carry
 * fewer than `pageSize` rows before its end; without `status`, `total` still
 * counts it, and the loaded rows can end short of `total`. A malformed cursor
 * is a 422 `{"detail": "invalid cursor"}`; any other `status` or `source`
 * value is a 422 with FastAPI's validation body, whose `detail` is an array.
 */
export interface GpoPharmacyPage {
  items: GpoPharmacyRow[];
  total: number;
  pageSize: number;
  nextCursor: string | null;
}

/**
 * Body of `POST /api/gpo/pharmacies`, the New pharmacy dialog's Submit. Mirrors
 * `GpoPharmacyCreateRequest` in server/app/models/api/gpo.py. The contact, who
 * becomes the pharmacy's owner, verifies one of the phone and email; the
 * other is taken as typed. A non-empty `npi` must pass the 80840 Luhn check,
 * the address follows the partner profile's address rules, and the phone and
 * email must parse (422 otherwise). The dialog carries no Terms & Conditions
 * acceptance: the pharmacy accepts on its partner-portal Organization profile.
 * The other errors:
 *   - 409 `already registered with DearBill`: a partner org has the NPI;
 *   - 409 `contact account disabled`: the matched owner account is disabled,
 *     and nothing is written;
 *   - 409 `contact email belongs to another account`: verified by phone, with
 *     an email that is another account's sign-in email; nothing is written;
 *   - 409 `contact phone belongs to another account`: verified by email, with
 *     a phone another live account holds; nothing is written;
 *   - 400 `verify the contact again`: the proof is missing, invalid, expired,
 *     minted for another GPO user, of another kind than `verifiedBy`, or for
 *     another value than the typed one;
 *   - 429: more than 60 creates per hour per GPO.
 */
export interface GpoPharmacyCreate {
  /**
   * `null` when the member skips the NPI; the server treats `""` and `null`
   * alike (no NPI, no duplicate check) and the pharmacy enters it in the partner
   * portal's Organization profile.
   */
  npi: string | null;
  /** Written to the org profile's name and legal name. */
  pharmacyName: string;
  address: PartnerOrgAddressInput;
  contactName: string;
  /**
   * As typed; the server normalises it to E.164. International numbers with a
   * leading `+` pass in the dialog; a public sign-up link takes US numbers only.
   */
  phone: string;
  /** As typed; the server lowercases and shape-checks it. */
  email: string;
  /** Which of `phone` and `email` the contact verified. */
  verifiedBy: "phone" | "email";
  /** `GpoPharmacySignupVerifyResponse.proof` from the `verifiedBy` code; valid 30 minutes. */
  proof: string;
}

/**
 * Body of a public sign-up link's `POST .../pharmacies`. Mirrors
 * `GpoLinkPharmacyCreateRequest` in server/app/models/api/gpo.py:
 * `GpoPharmacyCreate` with a US-only `phone` and the Terms & Conditions box,
 * which must be ticked (422 otherwise).
 */
export interface GpoLinkPharmacyCreate extends GpoPharmacyCreate {
  /** The "I agree to DearBill's Terms & Conditions" box, recorded as the pharmacy's Terms & Conditions acceptance. */
  termsAccepted: boolean;
}

/**
 * Body of `POST /api/gpo/pharmacies/verify/phone/send`, which sends the
 * contact's phone a code through Telnyx Verify. Mirrors
 * `GpoPharmacySignupPhoneSendRequest` in server/app/models/api/gpo.py. Returns `200
 * VerifySendResponse`; `422` for an unparseable phone; `429` within the
 * 30-second send cooldown for that phone (`detail: {message,
 * retryAfterSeconds}`, read with `retryAfterSecondsOf`) or over the GPO's
 * hourly cap on code sends (a string `detail`).
 */
export interface GpoPharmacySignupPhoneSendRequest {
  phone: string;
  channel?: OtpChannel;
}

/** Body of `POST /api/gpo/pharmacies/verify/phone`. Mirrors `GpoPharmacySignupPhoneVerifyRequest` in server/app/models/api/gpo.py. */
export interface GpoPharmacySignupPhoneVerifyRequest {
  phone: string;
  code: string;
}

/**
 * Body of `POST /api/gpo/pharmacies/verify/email/send`, which emails the
 * contact a 6-digit code valid 10 minutes. Mirrors
 * `GpoPharmacySignupEmailSendRequest` in server/app/models/api/gpo.py. The responses
 * match the phone send's, with `422` for a malformed email.
 */
export interface GpoPharmacySignupEmailSendRequest {
  email: string;
}

/** Body of `POST /api/gpo/pharmacies/verify/email`. Mirrors `GpoPharmacySignupEmailVerifyRequest` in server/app/models/api/gpo.py. */
export interface GpoPharmacySignupEmailVerifyRequest {
  email: string;
  code: string;
}

/**
 * Response of both sign-up verify endpoints: always a 200 with exactly one
 * non-null field. Mirrors `GpoPharmacySignupVerifyResponse` in
 * server/app/models/api/gpo.py. `proof` is an opaque token, valid 30 minutes,
 * that `GpoPharmacyCreate` carries. `reason` is `"rejected"` (wrong code),
 * `"expired"` (the code lapsed) or `"max_attempts_exceeded"` (too many wrong
 * codes, so a new code is needed).
 */
export interface GpoPharmacySignupVerifyResponse {
  proof: string | null;
  reason: string | null;
}

/**
 * Response of `GET /api/gpo/nppes-lookup?npi=…`, the New pharmacy flow's
 * prefill. Mirrors `GpoNppesLookupResponse` in server/app/models/api/gpo.py:
 * the `PartnerNppesLookupResponse` projection plus the NPPES
 * authorized-official `phone` and `alreadyRegistered`. A non-Luhn NPI is a
 * 422. `available: false` is an NPPES outage and still a 200; `resultCount ===
 * 0` is a successful lookup that found nothing. `organizationName` is null for
 * an individual NPI-1 record.
 */
export interface GpoNppesLookupResponse {
  available: boolean;
  resultCount: number;
  organizationName: string | null;
  /** Location address, which prefills the flow's address; null when the record carries none. */
  address: NpiNppesAddress | null;
  phone: string | null;
  /** A DearBill partner org already has this NPI, whatever NPPES answered. */
  alreadyRegistered: boolean;
}

/**
 * Body of `POST /api/gpo/pharmacies/invite-email`, which emails the signed-in
 * member's sign-up link to one address. Mirrors `GpoPharmacySignupInviteEmailRequest`
 * in server/app/models/api/gpo.py. The server builds the link from the
 * session, so the body carries only the address. Answers 204; 422 for a
 * malformed address; 502 when the email provider fails.
 */
export interface GpoPharmacySignupInviteEmailRequest {
  email: string;
}

export type PartnerRole = "org_admin" | "user";

/**
 * Partner account-level role, which governs who may create orgs. Distinct from
 * `PartnerRole`, the per-org membership role. `GET /api/partner/me` always
 * returns it, defaulting to `"user"`. Mirrors the `PartnerAccountRole` literal
 * in server/app/models/db/partner.py.
 */
export type PartnerAccountRole = "owner" | "user";

/**
 * Why an org is not yet active, as the server computes it (never stored): an
 * incomplete profile, an unaccepted Terms & Conditions, or both. The list never
 * includes the admin verification.
 */
export type PartnerOrgBlocker = "profile_incomplete" | "document_unsigned";

/**
 * Acceptance state of an org's Terms & Conditions. Mirrors
 * `PartnerDocumentStatus` in server/app/models/api/partner.py. Returned by
 * `GET /api/partner/orgs/{orgId}/document` (org admins only; 403 otherwise).
 * `signedAt` is an ISO 8601 UTC timestamp, null until accepted.
 */
export interface PartnerDocumentStatus {
  signed: boolean;
  signedAt: string | null;
}

/**
 * One org the authenticated partner user belongs to. `membershipActive` is the
 * per-membership activation flag. `orgActive` is the org's computed state:
 * profile complete, any required Terms & Conditions accepted, and admin-verified.
 * `blockers` lists the partner-visible reasons the org isn't active; it
 * excludes the admin verification, so it can be empty while `orgActive` is
 * false.
 */
export type PartnerSetupState =
  | "profile_needed"
  | "agreement_needed"
  | "review_in_progress"
  | "active"
  | "invitation_pending"
  | "unavailable";

export interface PartnerMembershipOut {
  orgId: string;
  orgName: string;
  role: PartnerRole;
  membershipActive: boolean;
  orgActive: boolean;
  blockers: PartnerOrgBlocker[];
  /**
   * Admin-controlled org disable flag, the server's
   * `PartnerMembershipOut.org_disabled`. The org list renders a disabled org
   * without a link, and its org-scoped `/api/partner/orgs/{orgId}/...` routes
   * return 403 with `{"detail": {"code": "org_disabled"}}`.
   */
  orgDisabled: boolean;
  setupState: PartnerSetupState;
}

/** Identity and memberships of the authenticated partner (`GET /api/partner/me`). */
export interface PartnerMeResponse {
  userId: string;
  email: string;
  name?: string | null;
  /** E.164 phone captured and OTP-verified at first-time setup; `""` when unset. */
  phone: string;
  accountRole: PartnerAccountRole;
  memberships: PartnerMembershipOut[];
}

/**
 * Response of `POST /api/partner/orgs/{orgId}/refid`, which mints a shareable
 * enrollment link tagged to the calling partner and org. `url` is the full
 * customer enrollment URL with the referral token in its `?refId=` param; the
 * portal opens it in a new tab. The caller must belong to an org that can take
 * referrals: not disabled, and active if it is a Pharmacy or Broker (403
 * otherwise).
 */
export interface PartnerRefIdResponse {
  url: string;
}

// Partner org profile: the org-profile form and its computed activation state,
// mirrored from server/app/models/api/partner.py. An org is "active" only when
// its profile is complete, any required Terms & Conditions are accepted, and an admin has
// verified it; `blockers` never includes the verification.
export type OrgType = "Provider" | "Pharmacy" | "Broker";
export type EntityType =
  | "Corporation (Inc)"
  | "Limited Liability Company (LLC)"
  | "Sole Proprietorship"
  | "General Partnership";
export type PartnerOrgStatus = "pending" | "active";

/**
 * Broker credentials. A Broker org has no NPI: its identity is a National
 * Producer Number plus per-state insurance licenses and a Line of Authority,
 * all verified manually by the admin with no registry check. Mirrors
 * `LineOfAuthority` in server/app/models/api/partner.py.
 */
export type LineOfAuthority = "Health" | "Accident & Health";
/** The Line of Authority select options on the Broker profile, in display order. */
export const LINES_OF_AUTHORITY: readonly LineOfAuthority[] = ["Health", "Accident & Health"];
/** One state insurance license: a `US_STATES` code plus the free-text license number, uppercased on save. */
export interface StateLicense {
  state: string;
  licenseNumber: string;
}

/** Org address on the PUT body; street1, city, state and zip are required server-side. */
export interface PartnerOrgAddressInput {
  street1: string;
  street2?: string;
  city: string;
  /** Valid US state code. */
  state: string;
  /** A 5-digit ZIP or ZIP+4 (the server's `_ZIP_RE`). */
  zip: string;
}

/**
 * Body for `PUT /api/partner/orgs/{orgId}/profile`, the strict Submit. A valid
 * save completes the profile, and the Submit that completes it or records the
 * Terms & Conditions acceptance locks it; a re-save after an admin unlock stays
 * unlocked. The org's phone and email come from its owner and are not part of
 * the body.
 */
export interface PartnerOrgProfileInput {
  legalName: string;
  address: PartnerOrgAddressInput;
  /** Legal entity structure; optional (`null`), and an off-list value is a 422. */
  entityType: EntityType | null;
  /**
   * Optional for every org type (`null` or `""`); a present value must be 10
   * digits passing the 80840-prefix Luhn check (422 otherwise). A blank NPI
   * skips the NPPES lookup.
   */
  npi?: string | null;
  dea?: string | null;
  /** Federal tax ID: `""` when omitted, else `XX-XXXXXXX` (the server's `_TAX_ID_RE`; 422 otherwise). */
  taxId: string;
  /** Broker only: the National Producer Number (the server's `_NPN_RE`); `""` and ignored for other org types. */
  npn: string;
  /** Broker only: at least one row, unique states, license numbers matching the server's `_LICENSE_RE`; `[]` otherwise. */
  stateLicenses: StateLicense[];
  /** Broker only: a required select; `null` otherwise. */
  lineOfAuthority: LineOfAuthority | null;
  /**
   * The "I agree to DearBill's Terms & Conditions" box; the server defaults it
   * to `false`. A Pharmacy or Broker org that has not accepted yet gets a 422
   * (`termsAccepted is required`) without it, and the server records the
   * acceptance in the same request with it.
   */
  termsAccepted: boolean;
}

/**
 * Body for `PUT /api/partner/orgs/{orgId}/profile/draft`, the lenient autosave
 * counterpart of the strict PUT above. Mirrors `PartnerOrgProfileDraftInput` in
 * server/app/models/api/partner.py.
 *
 * Every field takes raw form state, and blank or malformed values don't 422
 * (an off-list select value still does); the address reuses the all-string
 * prefill shape (`PartnerOrgAddressView`). Empty form selects (`""`) are sent
 * as `null`. Responds with the same `PartnerOrgProfileResponse` as the strict
 * PUT and GET. Server effects: no NPPES lookup; a changed NPI resets
 * `npiVerification` to `not_run`; a locked profile is a 409.
 */
export interface PartnerOrgProfileDraftInput {
  legalName: string;
  address: PartnerOrgAddressView;
  entityType: EntityType | null;
  npi: string | null;
  dea: string | null;
  /** Raw combined tax ID (`prefix-suffix`); blank or any string is accepted. */
  taxId: string;
  /** Broker fields, blank allowed; licenses are stored as typed. */
  npn: string;
  stateLicenses: StateLicense[];
  lineOfAuthority: LineOfAuthority | null;
}

/** Org address for prefill; any field may be empty. */
export interface PartnerOrgAddressView {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

/** Current profile values for prefill; any field may be empty. */
export interface PartnerOrgProfileView {
  legalName: string;
  address: PartnerOrgAddressView;
  /**
   * Org phone, filled when the org is created and editable only by staff;
   * `""` when unset.
   */
  phone: string;
  /** Org email, filled and edited like `phone`; `""` when unset. */
  email: string;
  /** Fixed at org creation; always present. */
  organizationType: OrgType;
  /** Legal entity structure; `null` when unset. */
  entityType: EntityType | null;
  npi: string;
  dea: string;
  /** Federal tax ID (`XX-XXXXXXX`); `""` until filled. */
  taxId: string;
  /** Broker credentials; `""`, `[]` and `null` for other org types. */
  npn: string;
  stateLicenses: StateLicense[];
  lineOfAuthority: LineOfAuthority | null;
}

// NPI verification: the NPPES-registry verdict on the wire, mirrored from
// `NpiVerificationView` and its nested snapshot views in
// server/app/models/api/partner.py. Carried on the partner profile response
// (`npiVerification`) and on the admin org detail, whose type in
// `admin/src/types/admin.ts` imports this shape. Always present with at least
// `status`: a never-verified org carries `status: "not_run"`, empty `flags`
// and all-null NPPES fields. The address keys are `address1` and `address2`,
// with no underscore, matching the server's wire view.

/** Verdict status for the NPI check. */
export type NpiVerificationStatus = "not_run" | "verified" | "flagged" | "unavailable";

/** One NPPES failure reason (populated on `status: "flagged"`). */
export type NpiFlag =
  | "npi_not_found"
  | "npi_inactive"
  | "npi_individual"
  | "npi_not_pharmacy"
  | "npi_identity_mismatch";

/** Primary taxonomy from the matched NPPES record. */
export interface NpiNppesTaxonomy {
  code: string;
  desc: string;
}

/** Location address from the matched NPPES record. */
export interface NpiNppesAddress {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
}

/** Snapshot of the matched NPPES record, for review. All fields null/empty when not run. */
export interface NpiNppesSnapshot {
  organizationName: string | null;
  otherNames: string[];
  /** "NPI-1" | "NPI-2". */
  enumerationType: string | null;
  /** "A" = active. */
  status: string | null;
  primaryTaxonomy: NpiNppesTaxonomy | null;
  locationAddress: NpiNppesAddress | null;
  authorizedOfficialPhone: string | null;
}

/** The structured NPI-registry verdict (always present; `not_run` when never checked). */
export interface NpiVerification {
  status: NpiVerificationStatus;
  /** The NPI that was checked (the bare profile NPI; `""` when never run). */
  npi: string;
  /** ISO 8601 timestamp of the last check; null when never run. */
  checkedAt: string | null;
  flags: NpiFlag[];
  nppes: NpiNppesSnapshot;
  /** rapidfuzz token-set-ratio; null when not computed. */
  nameMatchScore: number | null;
  addressMatchScore: number | null;
  /** Normalized-exact phone match; null when NPPES carried no authorized-official phone. */
  phoneMatch: boolean | null;
}

/**
 * Response of `GET /api/partner/orgs/{orgId}/nppes-lookup`: the org-profile
 * form's NPPES prefill. A thin projection of the NPPES record (the legal
 * business name and location address), never the full snapshot or verdict.
 * `available: false` is an NPPES outage with no cached record: still a 200,
 * shown as a non-blocking inline error. `available: true` with
 * `resultCount === 0` is a successful lookup that found nothing. Null means
 * absent, and the form overwrites only from present values; fields inside a
 * present `address` are plain strings and may be `""` when NPPES has nothing.
 */
export interface PartnerNppesLookupResponse {
  available: boolean;
  resultCount: number;
  /** Null when nothing is found, during an outage, and for an individual NPI-1 record. */
  organizationName: string | null;
  /** Location address to prefill; null when the record carries none. */
  address: NpiNppesAddress | null;
}

/**
 * The org's fixed owner: the account that created the org, or the first one it
 * was assigned to. The values are the owner account's, not the org profile's.
 * Mirrors `PartnerOrgOwnerView` in server/app/models/api/partner.py.
 */
export interface PartnerOrgOwnerView {
  name: string | null;
  email: string;
  /** The owner account's E.164 phone; `""` when it has none. */
  phone: string;
}

/** Response for GET + PUT `/api/partner/orgs/{orgId}/profile`. */
export interface PartnerOrgProfileResponse {
  orgId: string;
  /** Org display name, set at creation. */
  name: string;
  /** Caller's role in this org. */
  role: PartnerRole;
  profile: PartnerOrgProfileView;
  complete: boolean;
  status: PartnerOrgStatus;
  blockers: PartnerOrgBlocker[];
  /**
   * Whether the Terms & Conditions acceptance applies to this org (Pharmacy
   * and Broker orgs). The org-profile form shows the terms checkbox from this
   * rather than re-deriving it from `organizationType`.
   */
  agreementRequired: boolean;
  /**
   * Neutral "under review" signal: true when the partner-driven steps are done
   * (profile complete and any required acceptance recorded, so `blockers` is
   * empty) but an admin hasn't set verification to `verified`, so `status` is
   * still `pending`. The partner never sees the verification value; `flagged`
   * and `not_verified` both show as this same state, so the partner can't tell
   * them apart.
   */
  underReview: boolean;
  /** NPI-registry verdict; always present (`not_run` when never checked). */
  npiVerification: NpiVerification;
  /** Mirrors the super-admin `nppes_bypass` flag; the org-profile UI shows the dev NPI help tooltip when true. */
  nppesBypass: boolean;
  /**
   * Admin-controlled profile-edit lock, a standalone stored flag. A strict
   * Submit sets it when it completes the profile or records the Terms &
   * Conditions acceptance, but it isn't derived from either: an admin can lock
   * any org, and unlock one while the acceptance stands. While true the
   * profile is read-only and the profile `PUT` returns 409; only an admin
   * unlock re-enables editing.
   */
  profileLocked: boolean;
  /** The org's fixed owner; `null` for an org with no recorded owner. */
  owner: PartnerOrgOwnerView | null;
}

/** Per-member pharmacy introduction state. It does not record activation or consent. */
export type PartnerOnboardingStep =
  | "welcome"
  | "pharmacy_readiness"
  | "working_with_patients"
  | "next_action";

export interface PartnerOnboardingProgress {
  version: 1;
  status: "not_started" | "in_progress" | "deferred" | "completed";
  step: PartnerOnboardingStep;
  updatedAt: string | null;
}

/** Body of PUT `/api/partner/orgs/{orgId}/onboarding`. No patient or profile data. */
export interface PartnerOnboardingUpdate {
  version: 1;
  status: "in_progress" | "deferred" | "completed";
  step: PartnerOnboardingStep;
}

/** A caller-scoped tour preference, separate from introduction progress or training. */
export interface PartnerTourResponse {
  version: 1;
  seen: boolean;
}

/** Body of PUT `/api/partner/orgs/{orgId}/tour`. It only acknowledges a displayed tour. */
export interface PartnerTourUpdate {
  version: 1;
}

/**
 * Response of the OTP send path (Telnyx Verify, SMS or voice call). Mirrors
 * `VerifySendResponse` in server/app/models/api/verify.py. The portal login
 * resend and the GPO pharmacy sign-up's contact-phone send return it with `status`
 * `"pending"`, the default.
 */
export interface VerifySendResponse {
  status: string;
}

// In-portal member management: an org admin manages their own org's members
// from the partner portal. Mirrored from server/app/models/api/partner_admin.py;
// the admin SPA keeps its own `PartnerMemberOut` copy in
// `admin/src/types/admin.ts`. Backs GET and POST on
// `/api/partner/orgs/{orgId}/members`, and PATCH and DELETE on
// `/api/partner/orgs/{orgId}/members/{userId}`.

/**
 * One non-disabled member of an org, for the portal members table; the list
 * holds the owner first, then the rest by email. `status` is "active" once the
 * membership is active (an invited member activates by setting a password
 * through the invite link; an org's first org admin starts active), else
 * "pending". `name` is null until the member sets it.
 */
export interface PartnerMemberOut {
  userId: string;
  email: string;
  name: string | null;
  /** E.164 phone; "" until the member sets up (or the invite supplied one). */
  phone: string;
  role: PartnerRole;
  status: PartnerOrgStatus;
  /**
   * True for the org's owner, whose role is fixed and who cannot be removed:
   * PATCH and DELETE on them answer 403 `owner role is fixed`.
   */
  isOwner: boolean;
}

/**
 * Body of `POST /api/partner/orgs/{orgId}/members`; `role` defaults to "user".
 * `name` and `phone` are optional: stored on a freshly provisioned user row,
 * they prefill the invitee's set-password form, and the invitee can change
 * both there. `phone` is E.164-normalised server-side (422 when unparseable).
 */
export interface PartnerMemberCreate {
  name?: string;
  phone?: string;
  email: string;
  role?: PartnerRole;
}

/** Body of `PATCH /api/partner/orgs/{orgId}/members/{userId}`. */
export interface PartnerMemberRoleUpdate {
  role: PartnerRole;
}

// Partner enrollment read API: a referring partner views the enrollments their
// org referred. Mirrored from server/app/models/api/partner_enrollment.py.
// Reuses the shared `EnrollmentStatus` and `EsignStatus` unions and the
// `PermanentAddress`, `MailingAddress` and `Pharmacy` sub-types above. Backs
// `GET /api/partner/orgs/{orgId}/enrollments`
// (role-scoped: an org admin sees every org referral, a user only their own)
// and `GET /api/partner/orgs/{orgId}/enrollments/{enrollmentId}`.

/**
 * The partner-facing case-status vocabulary, carried on the wire as
 * `caseStatus`. The server derives it from the enrollment and e-sign statuses
 * (server/app/services/case_status.py). The label never names a program;
 * programs live in `caseStatusDetail` and `programStatus`.
 */
export type CaseStatus = "New" | "In progress" | "Engaged" | "Needs attention" | "Closed";

/** Every case status, in display order. */
export const CASE_STATUSES: readonly CaseStatus[] = [
  "New",
  "In progress",
  "Engaged",
  "Needs attention",
  "Closed",
];

/**
 * The program-status field: which program (M3P, LIS or PAP) and where it
 * stands. Mirrors `PartnerProgramStatus` in
 * server/app/models/api/partner_enrollment.py.
 */
export interface PartnerProgramStatus {
  program: "M3P" | "LIS" | "PAP";
  status: "Not assessed" | "Pending" | "Activated" | "Not proceeding";
}

/**
 * One shared row in the partner-portal enrollments table
 * (`GET .../enrollments`, newest first). `fullName` is
 * `"{firstName} {lastName}".trim()`, computed server-side. `partnerUserName`
 * and `partnerUserId` say who on the team referred it. String fields default
 * to "" when the value is unset. An unshared broker referral arrives as
 * `PartnerEnrollmentRedactedSummary` instead; see `PartnerEnrollmentRow`.
 */
export interface PartnerEnrollmentSummary {
  id: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  status: EnrollmentStatus;
  firstName: string;
  lastName: string;
  fullName: string;
  phone: string;
  mbi: string;
  /** Name of the assigned M3P plan; null until an admin assigns one. */
  planName: string | null;
  /** The contract year the plan was picked for; null reads as this year. */
  planYear: number | null;
  esignStatus: EsignStatus;
  /**
   * The case status, derived server-side. Nullable to mirror the backend model,
   * but always present on a shared row; an unshared broker referral arrives as
   * `PartnerEnrollmentRedactedSummary`, which has no case status.
   */
  caseStatus: CaseStatus | null;
  /** Plain-language "where it stands" line; nullable with `caseStatus`. */
  caseStatusDetail: string | null;
  /**
   * The program field; null when no program is named yet (draft, new,
   * need_more_information).
   */
  programStatus: PartnerProgramStatus | null;
  /**
   * Results-visibility gate and the `PartnerEnrollmentRow` discriminant: true
   * for every non-broker referral, and for a broker referral the patient's
   * "share my results with my broker" tick.
   */
  resultsShared: true;
  /** The partner team member who referred this enrollment. */
  partnerUserId: string;
  partnerUserName: string;
  /**
   * Computed live server-side from the referring org's current profile
   * completeness: true while that org's profile is incomplete. Always present
   * (false for an enrollment with no partner referrer). Render it straight off
   * the wire and never cache it client-side, so the warning marker disappears
   * as soon as the org profile is complete.
   */
  referrerProfileIncomplete: boolean;
  /**
   * ISO 8601 UTC: when the enrollment last changed. The newest status-history
   * stamp, or `createdAt` for a row with no manual status change yet; the
   * audit trail itself never reaches a partner. Nullable to mirror the backend
   * model, but always present on a shared row.
   */
  lastChangeAt: string | null;
}

/**
 * The name-and-date-only row for an unshared broker referral. The patient
 * declined results-sharing, so the server serialises an explicit allowlist:
 * `phone`, `mbi`, `status`, `planName` and `esignStatus` are absent from the
 * JSON, not null. The list shows "Not shared" for the withheld columns, and
 * the row doesn't link (the detail route 404s).
 */
export interface PartnerEnrollmentRedactedSummary {
  id: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  firstName: string;
  lastName: string;
  fullName: string;
  resultsShared: false;
  partnerUserId: string;
  partnerUserName: string;
  referrerProfileIncomplete: boolean;
}

/** One partner-list row, discriminated on `resultsShared`. */
export type PartnerEnrollmentRow = PartnerEnrollmentSummary | PartnerEnrollmentRedactedSummary;

/**
 * The partner list envelope: one newest-first page of at most the server's
 * `_PAGE_SIZE` rows, plus the opaque forward-only continuation cursor (null
 * when exhausted). Continue with `?cursor=<nextCursor>`; the tab appends pages
 * behind "Load more".
 */
export interface PartnerEnrollmentPage {
  items: PartnerEnrollmentRow[];
  nextCursor: string | null;
}

/**
 * The assigned M3P plan as the partner sees it: the plan name and its
 * catalogue insurer. Mirrors `PartnerAssignedPlan` in
 * server/app/models/api/partner_enrollment.py; the partner never sees the
 * plan's lifecycle state.
 */
export interface PartnerEnrollmentPlan {
  planName: string;
  insurerName: string;
}

/**
 * Full submitted enrollment data for one referred patient
 * (`GET .../enrollments/{enrollmentId}`), read-only. It carries the patient PHI
 * the partner submitted but none of the admin-internal fields (status-change
 * audit trail, admin notes, Medicare check, SMS consent, signed-doc and portal
 * URLs). Structural fields are typed leniently (`dob` and `gender` as plain
 * strings, optional nested sub-models), so a partial row degrades instead of
 * erroring, matching the backend model.
 */
export interface PartnerEnrollmentDetail {
  id: string;
  /** ISO 8601 UTC. */
  createdAt: string;
  status: EnrollmentStatus;

  firstName: string;
  lastName: string;
  fullName: string;
  suffix: string | null;
  pronouns: string | null;
  /** ISO date `YYYY-MM-DD`; "" if unset. */
  dob: string;
  gender: string;

  permanent: PermanentAddress | null;
  hasSeparateMailingAddress: boolean;
  mailing: MailingAddress | null;

  phone: string;
  homePhone: string | null;
  email: string | null;

  /** Assigned M3P plan; null until an admin assigns one. */
  plan: PartnerEnrollmentPlan | null;
  /** The contract year the plan was picked for; null reads as this year. */
  planYear: number | null;
  mbi: string;

  pharmacy: Pharmacy;
  /** Structured prescription list; always an array (backend defaults `[]`). */
  prescriptionsList: Prescription[];

  esignStatus: EsignStatus;
  /** ISO 8601 UTC; null until signed. */
  signedAt: string | null;

  // Case and program status, derived server-side. Never null here: an unshared
  // broker referral 404s the detail route.
  caseStatus: CaseStatus;
  caseStatusDetail: string;
  /** Null when no program is named yet (draft, new, need_more_information). */
  programStatus: PartnerProgramStatus | null;

  /** The partner team member who referred this enrollment. */
  partnerUserId: string;
  partnerUserName: string;

  /** ISO 8601 UTC: when the enrollment last changed (see the summary's field). */
  lastChangeAt: string | null;
}

// Partner cost-suggestion tool (M3P cost calculator), mirrored from the
// `/api/partner/orgs/{orgId}/cost-tool/*` models in
// server/app/models/api/cost_tool.py.

/**
 * One plan option from `GET /api/partner/orgs/{orgId}/cost-tool/plans?state=..&search=..`,
 * wrapped as `{ plans: [...] }`. Mirrors `CostToolPlanOption` in
 * server/app/models/api/cost_tool.py. `search` matches the plan name, the
 * insurer or a contract id like "H1234".
 */
export interface CostToolPlanOption {
  id: string;
  planName: string;
  organizationName: string;
  /** CMS contract number, e.g. "H1234". */
  cmsContractNumber: string;
  /** CMS plan/PBP id, e.g. "001". */
  cmsPlanId: string;
  category: string;
}

/** Anonymous catalogue search; mirrors PublicCostToolPlanRequest in public_cost_tool.py. */
export interface PublicCostToolPlanRequest {
  state: string;
  search?: string | null;
  planYear?: number | null;
}

/** Medicine search is sent in a POST body to keep it out of request URLs. */
export interface PublicCostToolDrugRequest {
  search: string;
}

/** Anonymous inputs only; mirrors PublicCostToolEstimateRequest in public_cost_tool.py. */
export interface PublicCostToolEstimateRequest {
  state: string;
  planId: string;
  rxcuis: string[];
  quantities?: Record<string, number> | null;
  /** Complete refill interval; omitted entries use 30 days. A 28-day interval requires a quantity. */
  fillDays?: Record<string, 28 | 30> | null;
  planYear?: number | null;
}

/** Complete refills purchased during the year, including supply beyond December 31. */
export interface AnnualCostToolDrug {
  rxcui: string;
  covered: boolean;
  quantityPerFill: number | null;
  quantityAssumed: boolean;
  daysSupply: 28 | 30;
  fillCount: number;
  totalQuantity: number | null;
  /** Complete refill costs before insurance; null when quantity or reference price is unknown. */
  grossCost: number | null;
  cost: number | null;
  deductiblePaid: number | null;
}

export interface AnnualCostToolEstimate {
  year: number;
  periodStart: string;
  periodEnd: string;
  coverageKnown: boolean;
  /** Before insurance, complete only when every selected medicine can be priced. */
  totalGrossCost: number | null;
  /** Sum of known gross costs; null when no selected medicine can be priced. */
  grossSubtotal: number | null;
  totalCost: number | null;
  deductible: number | null;
  oopThreshold: number;
  unavailableReason:
    | "plan_unknown"
    | "no_drugs"
    | "quantity_unknown"
    | "deductible_unknown"
    | "unpriced_drug"
    | null;
  drugs: AnnualCostToolDrug[];
  assumptions: Array<
    | "january_start"
    | "same_regimen_all_year"
    | "full_refills_including_carryover"
    | "no_prior_spending"
    | "no_other_drugs_or_subsidies"
    | "30_day_reference_pricing"
    | "request_order_for_same_day_fills"
  >;
}

/** Public response; one-fill fields retain their existing semantics. */
export interface PublicCostToolEstimate extends CostToolEstimate {
  annual: AnnualCostToolEstimate;
}

/**
 * One drug's row in the cost-tool estimate: coverage flags plus the per-fill
 * money, with fields degrading to null as in `M3pProjectionDrug`. Mirrors
 * `CostToolDrugRow` in server/app/models/api/cost_tool.py.
 */
export interface CostToolEstimateDrug {
  rxcui: string;
  drugName: string | null;
  label: string | null;
  covered: boolean;
  tier: number | null;
  /** Prior authorization required. */
  pa: boolean | null;
  /** Step therapy required. */
  st: boolean | null;
  /** Quantity limit applies. */
  ql: boolean | null;
  /** The one-fill plan price; null = unpriced. */
  gross: number | null;
  /** The slice of `cost` that went into the plan deductible. */
  deductiblePaid: number | null;
  /** The member's out-of-pocket for one fill with the deductible unmet: the high end. */
  cost: number | null;
  /** The same fill with the deductible already met: the low end. Null whenever `cost` is null. */
  costMin: number | null;
  /** cost / monthsRemaining. */
  monthly: number | null;
  /** costMin / monthsRemaining; null whenever `monthly` is null. */
  monthlyMin: number | null;
  /** True when `cost` is at or above the server's `HIGH_COST_FILL_THRESHOLD`; false when `cost` is null. */
  highCost: boolean;
  /** The per-fill quantity used (derived or overridden); null when unknown. */
  quantity: number | null;
  /** True when the engine derived `quantity` (or couldn't); false when the partner supplied it. */
  quantityAssumed: boolean;
}

/**
 * Response of `POST /api/partner/orgs/{orgId}/cost-tool/estimate`. Mirrors
 * `CostToolEstimate` in server/app/models/api/cost_tool.py.
 *
 * The body is `{planId, rxcuis: string[], quantities?: {[rxcui]: number},
 * planYear?: number}`; `quantities` carries only partner-edited per-fill
 * overrides (values > 0) and is omitted when there are none. `planYear` is the
 * contract year to estimate for: omitted means this year, and the next year is
 * accepted while `GET /api/plans/years` offers it (any other value is a 422).
 * The response gives the two numbers the pharmacist reads out, the biggest
 * single month without M3P and the level monthly with it, plus the drug
 * driving the spike. Never a 500: an unknown plan or an unpriced drug blanks
 * the figures and sets `unavailableReason`. The server assumes 30-day fills at
 * retail pharmacy pricing.
 *
 * Under a next-year estimate (`pricesEstimated`) the rules are `contractYear`'s
 * and the unit prices `priceYear`'s, with the clock at that January 1
 * (`startMonth` 1, `monthsRemaining` 12), as in `M3pProjection`.
 */
export interface CostToolEstimate {
  coverageKnown: boolean;
  /** SPUF quarter, e.g. "2026Q3"; null before the first refresh. */
  asOf: string | null;
  contractYear: number | null;
  /** True when the prices are the current year's applied to next year's rules (the next-year source). */
  pricesEstimated: boolean;
  /** The price table's contract year; null before its first ingest. */
  priceYear: number | null;
  deductible: number | null;
  oopThreshold: number | null;
  /** 1–12, the current month (server clock, UTC). */
  startMonth: number;
  /** 12 - startMonth + 1 (the current month counts; December = 1). */
  monthsRemaining: number;
  /** The biggest single month's out-of-pocket without M3P (deductible unmet, the high end); null = unknown. */
  biggestMonth: number | null;
  /** The same month with the deductible already met: the low end. Null whenever `biggestMonth` is null. */
  biggestMonthMin: number | null;
  /** The level M3P monthly amount (deductible unmet, the high end); null = unknown. */
  monthlyWithM3p: number | null;
  /** The same amount with the deductible already met: the low end. Null whenever `monthlyWithM3p` is null. */
  monthlyWithM3pMin: number | null;
  unavailableReason: string | null;
  /** The covered drug with the largest one-fill cost, which drives `biggestMonth`; null when no covered drug is priced. */
  drivingDrug: {
    rxcui: string;
    drugName: string | null;
    label: string | null;
    cost: number | null;
  } | null;
  /** One per requested rxcui, request order (`covered: false` rows included). */
  drugs: CostToolEstimateDrug[];
  verdict: "helps" | "no_help" | "unknown";
}

/**
 * One HTML form an ops fill session can be launched for. Mirrors
 * `FillFormSummary` in server/app/models/api/fill.py; the wire format of the
 * fill endpoints is snake_case. Response of `GET /api/admin/fill-forms`.
 */
export interface FillFormSummary {
  form_id: string;
  display_name: string;
  start_url: string;
}

/** Body of `POST /api/admin/fill-sessions`. Mirrors `FillSessionCreate` in server/app/models/api/fill.py. */
export interface FillSessionCreate {
  patient_id: string;
  enrollment_id: string;
  form_id: string;
}

/**
 * The 201 response of `POST /api/admin/fill-sessions`. Mirrors
 * `FillSessionCreated` in server/app/models/api/fill.py. `fill_token` is a
 * 60-minute bearer for the `/fill/session` routes, scoped to one form and one
 * enrollment; the admin SPA hands it to the ops extension and keeps no copy.
 */
export interface FillSessionCreated {
  fill_token: string;
  session_id: string;
  form_id: string;
  start_url: string;
  /** ISO 8601, UTC. */
  expires_at: string;
}
